import * as amplitude from '@amplitude/analytics-browser'
import { Identify } from '@amplitude/analytics-browser'

/* =====================================================
   Configuration Object - Extracted hardcoded values
   ===================================================== */
const CONFIG = {
    MENU_MAPPINGS: {
        'point': 'Points',
        'points': 'Points',
        'inquiry': 'Inquiries',
        'membership': 'Membership',
        'invoice': 'Invoice',
        'company': 'Company',
        'corporate info update': 'Corporate Info Update',
        'corporate': 'Corporate',
        'contact': 'Contact',
        'promotion': 'Promotion Code',
        'outstanding': 'Outstanding',
        'coupons': 'Coupons',
        'coupon': 'Coupons',
        'location': 'Location',
        'locations': 'Location',
        'make/vehicle': 'Vehicle Make',
        'model/vehicle': 'Vehicle Model',
        'attribute/vehicle': 'Vehicle Attribute',
        'vehicle/testing': 'Vehicle Testing',
        'vehicle/archived': 'Archived Vehicles',
        'our-vehicle': 'Our Vehicle',
        'vehiclemanagement/schedule': 'Vehicle Schedule',
        'vehiclemanagement': 'Vehicles',
        'reservation': 'Reservations',
        'reservations': 'Reservations',
        'corporate reservation': 'Corporate Reservations',
        'canceled reservation': 'Canceled Reservations',
        'owr reservation': 'OWR Reservations',
        'fuel claim': 'Fuel Claim Reservations',
        'damage alert': 'Damage Alert Reservations',
        'review reservation': 'Review Reservations',
        'recharge reservation': 'Recharge Reservations',
        'outstanding reservation': 'Outstanding Reservations',
        'refund membership': 'Refund Membership',
        'customer': 'Customers',
        'customers': 'Customers',
        'role': 'Role',
        'roles': 'Role',
        'adminmanagement': 'Admin Management',
        'admin management': 'Admin Management',
        // Rate Management (prefix ratemanagement — match longer paths first)
        'ratemanagement/exchangerate': 'Exchange Rate',
        'ratemanagement/owrrate': 'OWR Rate',
        'ratemanagement/acriss': 'Acriss',
        'ratemanagement/rate': 'Rates',
        notification: 'Notification'
    },
    ENTITY_ID_MAP: {
        points: 'point_id',
        point: 'point_id',
        membership: 'membership_id',
        company: 'company_id',
        invoice: 'invoice_id',
        inquiry: 'inquiry_id',
        // Contact + corporate enquiry UIs (Inquiries nav): single id property for Amplitude
        contact: 'inquiry_id',
        corporate: 'inquiry_id',
        corporate_enquiry: 'inquiry_id',
        promotion: 'promotion_id',
        coupon: 'coupon_id',
        outstanding: 'outstanding_id',
        location: 'location_id',
        locations: 'location_id',
        reservation: 'reservation_id',
        reservations: 'reservation_id',
        reservationfeedback: 'reservation_id',
        outstandingreservation: 'reservation_id',
        reservationadditionalcharges: 'reservation_id',
        reservationapproval: 'reservation_id',
        reservationdiscount: 'reservation_id',
        reservationdriver: 'reservation_id',
        reservationfreezingpoint: 'reservation_id',
        reservationhardwarehistory: 'reservation_id',
        reservationhistory: 'reservation_id',
        reservationmanual: 'reservation_id',
        reservationrate: 'reservation_id',
        reservationrefundpromotion: 'reservation_id',
        reservationrentchecklist: 'reservation_id',
        reservationreport: 'reservation_id',
        reservationreportimage: 'reservation_id',
        reservationvehiclechangehistory: 'reservation_id',
        damagealert: 'reservation_id',
        subscription: 'id',
        customer: 'customer_id',
        customers: 'customer_id',
        role: 'role_id',
        roles: 'role_id',
        admin: 'admin_id',
        admins: 'admin_id',
        vehicle: 'vehicle_id',
        vehicle_make: 'vehicle_make_id',
        vehicle_model: 'vehicle_model_id',
        vehicle_attribute: 'vehicle_attribute_id',
        our_vehicle: 'our_vehicle_id',
        rate: 'rate_id',
        acriss: 'acriss_id',
        exchange_rate: 'exchange_rate_id',
        exchangerate: 'exchange_rate_id',
        notification: 'notification_id'
    },
    ACTION_LABELS: {
        delete: {
            soft: 'Delete',
            permanent: 'Delete Permanently'
        },
        sync: 'Synced',
        restore: {
            deleted: 'Restore Deleted',
            sync: 'Sync'
        }
    },
    RETRY_INTERVAL: 300,
    SEARCH_DEBOUNCE: 500
};

/**
 * When `data-amplitude-entity` is missing, infer from Blade `type` + URL (same idea as legacy jQuery.data() flows).
 */
window.resolveGlobalDeleteEntity = function (explicitEntity, typeAttr, pathname) {
    const ex =
        explicitEntity != null && String(explicitEntity).trim() !== ''
            ? String(explicitEntity).trim().toLowerCase()
            : '';
    if (ex) return ex;

    const t = String(typeAttr != null ? typeAttr : '').trim().toLowerCase();
    const path = String(
        pathname != null
            ? pathname
            : typeof location !== 'undefined'
              ? location.pathname || ''
              : ''
    ).toLowerCase();

    if (!t) return '';

    if (t === 'data') {
        if (path.includes('/companymanagement')) return 'company';
        if (path.includes('/notification')) return 'notification';
        if (path.includes('/membershipmanagement')) return 'membership';
        if (path.includes('/pointmanagement')) return 'point';
        if (path.includes('/invoices')) return 'invoice';
        if (path.includes('/corporate')) return 'corporate';
        return 'data';
    }

    if (t === 'delete') {
        if (path.includes('/attribute')) return 'vehicle_attribute';
        if (path.includes('/model')) return 'vehicle_model';
        return 'delete';
    }

    const typeMap = {
        vehicle: 'vehicle',
        location: 'location',
        promotion: 'promotion',
        model: 'vehicle_model',
        make: 'vehicle_make',
        coupon: 'coupon',
        rate: 'rate',
        user: 'customer',
        admin: 'admin',
        enquiry: 'inquiry',
        our_vehicle: 'our_vehicle',
        'our-vehicle': 'our_vehicle',
        'emergency-contact': 'customer',
        'extra-driver': 'customer',
    };

    return typeMap[t] || t;
};

/* =====================================================
   amplitude.js — GLOBAL (Amplitude Browser SDK v2 CDN)
   Laravel + Blade + jQuery + DataTables
   ===================================================== */

class AmplitudeTracker {
    constructor() {
        this.initialized = false;
        this._eventBindingsRegistered = false;
        this._lastSearchSignature = '';
        this._hasUserSearched = false;
    }

    flush() {
        if (!this.initialized) return;
        try {
            amplitude.flush();
        } catch (error) {
            console.error('[Amplitude] Flush error:', error);
        }
    }

    /**
     * Safe tracking wrapper with error handling
     * @param {string} eventName - Event name to track
     * @param {object} properties - Event properties
     * @returns {boolean} - Success status
     */
    _safeTrack(eventName, properties = {}) {
        try {
            if (!this.initialized) {
                console.warn('[Amplitude] Tracker not initialized, event:', eventName);
                return false;
            }

            if (!eventName || typeof eventName !== 'string') {
                console.warn('[Amplitude] Invalid event name:', eventName);
                return false;
            }

            return this.track(eventName, properties);
        } catch (error) {
            console.error('[Amplitude] Tracking error:', error, { eventName, properties });
            return false;
        }
    }

    /* ================= INIT ================= */

    init() {
        // Register window.* delete/restore handlers and delegated listeners even when API key
        // is missing, so SweetAlert flows can call them once the key is present on a later init.
        if (!this._eventBindingsRegistered) {
            this._initEventBindings();
            this._eventBindingsRegistered = true;
        }

        if (this.initialized) return;

        // Admin layout sets window.APP_CONFIG.AMPLITUDE_API_KEY; guest pages (login, errors) often set window.AMPLITUDE_API_KEY only.
        const apiKey =
            (window.APP_CONFIG && window.APP_CONFIG.AMPLITUDE_API_KEY) ||
            window.AMPLITUDE_API_KEY ||
            '';
        if (!apiKey) {
            console.warn('[Amplitude] API key missing');
            return;
        }

        amplitude.init(apiKey, {
            defaultTracking: false,
            logLevel: amplitude.Types.LogLevel.Warn,
            transport: 'beacon'
        });

        this.initialized = true;
        this._initNavTracking();
    }

    /* ================= NAVIGATION TRACKING ================= */
    _initNavTracking() {
        const sidebar = document.getElementById('primary-sidebar')
        if (!sidebar) return

        // Event delegation (IMPORTANT)
        sidebar.addEventListener('click', (e) => {
            const link = e.target.closest('a.nav-link:not(.nav-group-toggle)')
            if (!link) return

            let menuName = link.textContent.trim()
            menuName = this._normalizePrimarySidebarNavMenuName(link, menuName)

            let navTitle = this._findNavTitle(link)
            const navTitleNorm = this._normalizeNavSectionTitle(navTitle)

            const eventName = navTitleNorm
                ? `${navTitleNorm} - Click ${menuName}`
                : `Navigation - Click ${menuName}`

            this.track(eventName, {
                menu_name: menuName,
                nav_title: navTitleNorm ?? 'No Section',
                menu_url: link.href,
                from_page: window.location.pathname,
            })
        })
    }

    
    _findNavTitle(link) {
        let el = link.closest('li.nav-item')
        
        // Walk backwards through siblings
        while (el) {
            let prev = el.previousElementSibling
            
            while (prev) {
                // Found a nav-title, return it
                if (prev.classList && prev.classList.contains('nav-title')) {
                    return prev.textContent.trim()
                }
                
                // If we hit a divider and haven't found a title yet,
                // check what's before the divider
                if (prev.classList && prev.classList.contains('sidebar-divider')) {
                    // Look further back for a title
                    let beforeDivider = prev.previousElementSibling
                    let hasTitleBeforeDivider = false
                    
                    while (beforeDivider) {
                        if (beforeDivider.classList && beforeDivider.classList.contains('nav-title')) {
                            hasTitleBeforeDivider = true
                            break
                        }
                        beforeDivider = beforeDivider.previousElementSibling
                    }
                    
                    // If no title before divider, this is a standalone section
                    if (!hasTitleBeforeDivider) {
                        return link.textContent.trim() // "Dashboard" or "Scheduler"
                    }
                }
                
                prev = prev.previousElementSibling
            }
            
            // Move up to parent li if exists
            el = el.parentElement?.closest('li.nav-item')
        }

        // If the link lives inside a nav-group-items <ul> (like "Outstanding"),
        // the nav-title is a sibling of that <ul>, not of the <li.nav-item>.
        // Walk backwards from the group container to find the nearest nav-title.
        const groupList = link.closest('ul.nav-group-items')
        if (groupList) {
            let prev = groupList.previousElementSibling
            while (prev) {
                if (prev.classList && prev.classList.contains('nav-title')) {
                    return prev.textContent.trim()
                }
                if (prev.classList && prev.classList.contains('sidebar-divider')) break
                prev = prev.previousElementSibling
            }

            // CoreUI "Vehicle Builders": <li.nav-group> holds .nav-group-toggle then ul.nav-group-items.
            // previousElementSibling of the <ul> is the toggle <a>, not nav-title — walk up to li.nav-group.
            const navGroupLi = groupList.closest('li.nav-group')
            if (navGroupLi) {
                prev = navGroupLi.previousElementSibling
                while (prev) {
                    if (prev.classList && prev.classList.contains('nav-title')) {
                        return prev.textContent.trim()
                    }
                    if (prev.classList && prev.classList.contains('sidebar-divider')) break
                    prev = prev.previousElementSibling
                }
            }
        }

        // Fallback: if no nav-title found, use the link text
        return link.textContent.trim()
    }

    /**
     * Sidebar labels under Vehicle Builders are short ("Makes", "Model", "Attributes").
     * Prefer stable event names: Vehicles - Click Vehicle Make | Vehicle Model | Vehicle Attribute.
     */
    _normalizePrimarySidebarNavMenuName(link, menuNameRaw) {
        const href = (link.getAttribute('href') || '').toLowerCase()
        const path = href.replace(/^https?:\/\/[^/]+/i, '')

        if (path.includes('vehiclemanagement/make') || path.includes('vehicle-make')) {
            return 'Vehicle Make'
        }
        if (path.includes('vehiclemanagement/model') || path.includes('vehicle-model')) {
            return 'Vehicle Model'
        }
        if (path.includes('vehiclemanagement/attribute')) {
            return 'Vehicle Attribute'
        }

        return menuNameRaw
    }

    /**
     * When _findNavTitle falls back to link text, Vehicle Builders items become "Makes"/"Model"/"Attributes"
     * — treat those as section Vehicles for the event prefix.
     */
    _normalizeNavSectionTitle(title) {
        if (title == null || title === '') return title
        const key = String(title).trim().toLowerCase()
        if (
            key === 'makes' ||
            key === 'make' ||
            key === 'model' ||
            key === 'models' ||
            key === 'attributes' ||
            key === 'attribute'
        ) {
            return 'Vehicles'
        }
        if (key === 'admin management') {
            return 'System'
        }
        return String(title).trim()
    }

    /* ================= USER ================= */

    setUser(email, userData = {}) {
        try {
            if (!this.initialized) {
                console.warn('[Amplitude] Tracker not initialized, cannot set user');
                return false;
            }

            if (!email || typeof email !== 'string') {
                console.warn('[Amplitude] Invalid email provided:', email);
                return false;
            }

            amplitude.setUserId(email);

            const identify = new Identify();
            // Clear lowercase keys from older identifies so Amplitude does not show duplicate email/role.
            identify.unset('email');
            identify.unset('role');
            const roleStr =
                userData?.role != null && String(userData.role).trim() !== ''
                    ? String(userData.role).trim()
                    : 'unknown';
            identify
                .set('Email', email)
                .set('name', userData?.name ?? null)
                .set('Role', roleStr)
                .set('original_user_id', userData?.id ?? null);

            const displayName = userData?.userName ?? userData?.name;
            if (displayName != null && String(displayName).trim() !== '') {
                identify.set('User Name', String(displayName).trim());
            }

            amplitude.identify(identify);
            return true;
        } catch (error) {
            console.error('[Amplitude] Error setting user:', error);
            return false;
        }
    }


    track(eventName, properties = {}) {
        try {
            if (!this.initialized) {
                console.warn('[Amplitude] Tracker not initialized');
                return false;
            }

            if (!eventName || typeof eventName !== 'string') {
                console.warn('[Amplitude] Invalid event name:', eventName);
                return false;
            }

            const { entity, entity_id, vehicle_id: propVehicleId, ...rest } = properties || {};

            const payload = {
                page_title: document.title || '',
                path: location.pathname || '',
                url: location.href || '',

                // inject dynamic entity id key
                ...buildEntityIdProperty(entity, entity_id),

                // keep everything else
                entity,
                ...rest
            };

            if (isVehicleManagementAmplitudeContext()) {
                payload.vehicle_id = resolveVehicleIdForVehicleAreaEvent(
                    propVehicleId,
                    entity,
                    entity_id
                );
            }

            amplitude.track(eventName, payload);

            return true;
        } catch (error) {
            console.error('[Amplitude] Track error:', error, { eventName, properties });
            return false;
        }
    }


    /**
     * Generic action tracker to reduce code duplication
     * @param {string} actionType - Type of action (delete, sync, restore)
     * @param {string} entity - Entity name
     * @param {string|number} entityId - Entity ID
     * @param {object} options - Additional options
     * @returns {object} - Object with tracking methods
     */
    _trackAction(actionType, entity, entityId, options = {}) {
        const {
            actionLabel,
            successLabel,
            failLabel,
            confirmLabel,
            cancelLabel,
            extraProps = {},
            moduleName: moduleNameOverride,
            menuName: menuNameOverride
        } = options;
        const moduleName = moduleNameOverride || (
            typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown'
        );
        const menuName = menuNameOverride || (
            typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown'
        );

        const baseProps = {
            source: `${entity}_list`,
            entity: entity || 'unknown',
            entity_id: entityId || null,
            ...extraProps
        };

        return {
            click: () => this._safeTrack(
                `${moduleName} - Click ${actionLabel} ${menuName}`,
                { ...baseProps, action_type: actionType, _ts: Date.now() }
            ),
            confirm: () => this._safeTrack(
                `${moduleName} - Click Confirm ${confirmLabel || actionLabel} ${menuName}`,
                { ...baseProps, action_type: actionType }
            ),
            cancel: () => this._safeTrack(
                `${moduleName} - Click Cancel ${cancelLabel || actionLabel} ${menuName}`,
                { ...baseProps, action_type: actionType }
            ),
            success: () => {
                const tracked = this._safeTrack(
                    `${moduleName} - ${successLabel || `Confirm ${actionLabel}`} ${menuName} Success`,
                    { ...baseProps, action_type: actionType }
                );
                if (tracked) this.flush();
                return tracked;
            },
            fail: (error = null) => {
                const tracked = this._safeTrack(
                    `${moduleName} - ${failLabel || `Confirm ${actionLabel}`} ${menuName} Fail`,
                    { 
                        ...baseProps, 
                        action_type: actionType,
                        error_code: error?.code || null,
                        error_message: error?.message || error || null
                    }
                );
                if (tracked) this.flush();
                return tracked;
            }
        };
    }

    /* ================= TABLE EVENTS ================= */
    table = {
        export: (tableName, exportType) => {
            const moduleName = getCurrentModule();
            return window.amplitudeTracker.track(`${moduleName} - Click Export`, {
                source: tableName,
                export_type: exportType
            });
        },

        search: (tableName, column, search_keyword) => {
            const moduleName = getCurrentModule();
            let menuName = getCurrentMenu();
            if (String(moduleName) === 'Customers') {
                const detailTab = getCurrentCustomerDetailTab();
                if (detailTab) menuName = detailTab;
            }
            const searchTarget =
                menuName && menuName !== 'Unknown' ? menuName : moduleName;

            return window.amplitudeTracker.track(`${moduleName} - Search ${searchTarget}`, {
                source: tableName,
                column,
                search_keyword
            });
        },

        filter: (tableName, column, value) => {
            const moduleName = getCurrentModule();
            return window.amplitudeTracker.track(`${moduleName} - Filter`, {
                table_name: tableName,
                column,
                value
            });
        },

        sort: (tableName, sort, column_name) => {
            const moduleName = getCurrentModule();

            return window.amplitudeTracker.track(`${moduleName} - Sort Column`, {
                source: tableName,
                sort: sort,
                column_name: column_name
            });
        },


        pageChange: (tableName, page) => {
            const moduleName = getCurrentModule();
            return window.amplitudeTracker.track(`${moduleName} - Page Change`, {
                table_name: tableName,
                page
            });
        },

        rowsPerPage: (tableName, num_of_row) => {
            const moduleName = getCurrentModule();
            return window.amplitudeTracker.track(`${moduleName} - Select Row Per Page`, {
                table_name: tableName,
                num_of_row: Number(num_of_row)
            });
        },

        clickAction: (tableName) => {
            const moduleName = getCurrentModule();
            return window.amplitudeTracker.track(`${moduleName} - Click Action`, {
                table_name: tableName
            });
        },

        choosePageNo: (tableName, page_no) => {
            const moduleName = getCurrentModule();
            return window.amplitudeTracker.track(`${moduleName} - Choose Page No`, {
                table_name: tableName,
                page_no: Number(page_no)
            });
        }
    };

    /* ================= SEARCH RESULT ================= */
    searchResult = {
        success: (count) => {
            const moduleName = getCurrentModule();
            let menuName = getCurrentMenu();
            if (String(moduleName) === 'Customers') {
                const detailTab = getCurrentCustomerDetailTab();
                if (detailTab) menuName = detailTab;
            }
            const searchTarget = (() => {
                const label = String(menuName || '').trim();
                if (!label) return 'Unknown';
                if (label.toLowerCase().endsWith('ss')) return label; // e.g. "Address" -> doesn't apply, but avoid "ss" cases
                if (label === 'Points') return 'Points';
                if (label.toLowerCase().endsWith('ies')) return label.slice(0, -3) + 'y'; // Inquiries -> Inquiry
                if (label.toLowerCase().endsWith('s')) return label.slice(0, -1); // Points -> Point, Reviews -> Review
                return label;
            })();

            return window.amplitudeTracker.track(
                `${moduleName} - Search ${searchTarget} Success`,
                {
                    result_count: Number(count),
                }
            );
        },

        fail: (code, message) => {
            const moduleName = getCurrentModule();
            let menuName = getCurrentMenu();
            if (String(moduleName) === 'Customers') {
                const detailTab = getCurrentCustomerDetailTab();
                if (detailTab) menuName = detailTab;
            }
            const searchTarget = (() => {
                const label = String(menuName || '').trim();
                if (!label) return 'Unknown';
                if (label.toLowerCase().endsWith('ss')) return label;
                if (label === 'Points') return 'Points';
                if (label.toLowerCase().endsWith('ies')) return label.slice(0, -3) + 'y';
                if (label.toLowerCase().endsWith('s')) return label.slice(0, -1);
                return label;
            })();

            return window.amplitudeTracker.track(
                `${moduleName} - Search ${searchTarget} Fail`,
                {
                    error_code: String(code),
                    error_message: String(message),
                }
            );
        }
    };
    


/* ================= EVENT BINDINGS ================= */
_initEventBindings() {
    
    /* ================= HEADER EVENTS ================= */
    // Theme preference tracking
    document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="theme-switch"]')) {
            const themeType = e.target.value;
            this._safeTrack('General - Choose Preference', { type: themeType });
        }
    });

    // Header action tracking (menu toggle, notification, tabs, close)
    document.addEventListener('click', (e) => {
        const target = e.target.closest('[data-amplitude-action]');
        if (!target) return;

        const action = target.dataset.amplitudeAction;
        
        // Handle header-specific actions
        switch (action) {
            case 'Menu Toggle':
                const sidebarElement = document.querySelector('#sidebar');
                const isHidden = sidebarElement.classList.contains('hide');
                this._safeTrack('General - Click Menu Toggle', { type: isHidden ? 'show' : 'hide' });
                return; // Stop propagation to avoid second listener
                
            case 'Notification':
                this._safeTrack('General - Click Notification', { button_clicked: 'notification' });
                return;
                
            case 'Activities':
                this._safeTrack('General - Click Activities', { button_clicked: 'Activities' });
                return;
                
            case 'Offers':
                this._safeTrack('General - Click Offers', { button_clicked: 'Offers' });
                return;
                
            case 'Feeds':
                this._safeTrack('General - Click Feeds', { button_clicked: 'Feeds' });
                return;
                
            case 'Close Tab':
                this._safeTrack('General - Click Close Tab');
                return;
        }
    });

    // Reservation find button tracking
    window.trackReservationFindClick = function() {
        if (!window.amplitudeTracker) return;
        
        const formData = {
            user_id: $('#user_id-select2')?.val() || null,
            pickup_at: $('#reservation_pickup_date')?.val() + ' ' + ($('#reservation_pickup_time')?.val() || ''),
            return_at: $('#reservation_return_date')?.val() + ' ' + ($('#reservation_return_time')?.val() || ''),
            pickup_location: $('#pickup_location')?.val() || null,
            return_location: $('#return_location')?.val() || null,
            transaction_type: $('input[name="transaction_type"]:checked')?.val() || null,
            reservation_rate_id: $('input[name="reservation_rate"]:checked')?.val() || null,
            vehicle_name: $('#vehicle-name')?.val() || null
        };
        
        window.amplitudeTracker._safeTrack('Reservations - Click Find', formData);
    };

    // Reservation find success/fail tracking
    window.trackReservationFindResult = function(success, error = null) {
        if (!window.amplitudeTracker) return;
        
        if (success) {
            window.amplitudeTracker._safeTrack('Reservations - Find Success');
        } else {
            window.amplitudeTracker._safeTrack('Reservations - Find Fail', {
                error_code: error?.code || null,
                error_message: error?.message || error || null
            });
        }
    };

    // Reservation car category search tracking
    window.trackReservationCarCategorySearch = function(searchKeyword) {
        if (!window.amplitudeTracker) return;
        
        window.amplitudeTracker._safeTrack('Reservations - Search Car Category', {
            search_keyword: searchKeyword || ''
        });
    };

    // Reservation car category search result tracking
    window.trackReservationCarCategoryResult = function(success, resultCount = null, error = null) {
        if (!window.amplitudeTracker) return;
        
        if (success) {
            window.amplitudeTracker._safeTrack('Reservations - Search Car Category Success', {
                search_result_count: resultCount || 0
            });
        } else {
            window.amplitudeTracker._safeTrack('Reservations - Search Car Category Fail', {
                error_code: error?.code || null,
                error_message: error?.message || error || null
            });
        }
    };

    // Reservation car selection tracking
    window.trackReservationSelectCar = function(uniqueId, categoryName) {
        if (!window.amplitudeTracker) return;
        
        window.amplitudeTracker._safeTrack('Reservations - Select Car Category', {
            unique_id: uniqueId || null,
            category_name: categoryName || null
        });
    };

    // Reservation remove car category tracking
    window.trackReservationRemoveCarCategory = function() {
        if (!window.amplitudeTracker) return;
        
        window.amplitudeTracker._safeTrack('Reservations - Remove Car Category');
    };

    // Reservation make payment tracking
    window.trackReservationMakePayment = function(category, vehicleName) {
        if (!window.amplitudeTracker) return;
        
        window.amplitudeTracker._safeTrack('Reservations - Click Make Payment', {
            category: category || null,
            vehicle_name: vehicleName || null
        });
    };

    // Reservation make payment click tracking (gets selected vehicle info)
    window.trackReservationMakePaymentClick = function() {
        if (!window.amplitudeTracker) return;
        
        const selectedVehicle = $('input[name="vehicle_id"]:checked');
        const category = $('#category').val() || '';
        const vehicleName = selectedVehicle.length ? selectedVehicle.closest('.vehicle-selector').find('.vehicle-selector-title').text().trim() : '';
        
        window.amplitudeTracker._safeTrack('Reservations - Click Make Payment', {
            category: category === 'ALL' ? 'ALL' : category,
            vehicle_name: vehicleName
        });
    };

    // Reservation make payment result tracking (create-reservation API after payment step — wizard step 2 in product funnel)
    window.trackReservationMakePaymentResult = function(success, error = null) {
        if (!window.amplitudeTracker) return;

        const base = { reservation_wizard_step: 2 };

        if (success) {
            window.amplitudeTracker._safeTrack('Reservations - Make Payment Success', base);
        } else {
            window.amplitudeTracker._safeTrack('Reservations - Make Payment Fail', {
                ...base,
                error_code: error?.code || null,
                error_message: error?.message || error || null
            });
        }
    };

    // Reservation back button tracking
    window.trackReservationBackFindCar = function() {
        if (!window.amplitudeTracker) return;
        
        window.amplitudeTracker._safeTrack('Reservations - Click Back Find Car');
    };

    // Reservation generate quotation tracking (wizard step 2 — same funnel as vehicle / payment flow)
    window.trackReservationGenerateQuotation = function() {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Generate Quotation', {
            reservation_wizard_step: 2
        });
    };

    // Reservation cancellation invoice tracking
    window.trackReservationClickGenerateCancellationInvoice = function(reservationId) {
        if (!window.amplitudeTracker) return;
        
        window.amplitudeTracker._safeTrack('Reservations - Click Generate Cancellation Invoice', {
            reservation_id: reservationId || null
        });
    };

    window.trackReservationConfirmGenerateCancellationInvoice = function(reservationId) {
        if (!window.amplitudeTracker) return;
        
        window.amplitudeTracker._safeTrack('Reservations - Click Confirm Generate Cancellation Invoice', {
            reservation_id: reservationId || null
        });
    };

    window.trackReservationClickCancelCancellationInvoice = function(reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack(
            'Reservations - Click Cancel Cancellation Invoice',
            {
                reservation_id: reservationId || null
            }
        );
        window.amplitudeTracker.flush?.();
    };

    window.trackReservationGenerateCancellationInvoiceResult = function(success, reservationId, error = null) {
        if (!window.amplitudeTracker) return;
        
        if (success) {
            window.amplitudeTracker._safeTrack('Reservations - Generate Cancellation Invoice Success', {
                reservation_id: reservationId || null
            });
            window.amplitudeTracker.flush?.();
        } else {
            window.amplitudeTracker._safeTrack('Reservations - Generate Cancellation Invoice Fail', {
                error_code: error?.code || null,
                reservation_id: reservationId || null,
                error_message: error?.message || error || null
            });
            window.amplitudeTracker.flush?.();
        }
    };

    window.trackReservationClickRate = function (reservationId, invoiceNumber) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Rate', {
            reservation_id: reservationId || null,
            invoice_number: invoiceNumber || null,
        });
    };

    window.trackReservationClickPaymentDetail = function (invoiceNumber, orderId, transactionNumber) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Payment Detail', {
            invoice_number: invoiceNumber || null,
            order_id: orderId || null,
            transaction_number: transactionNumber || null,
        });
    };

    window.trackReservationClickViewInvoice = function (language, invoiceNumber) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click View Invoice', {
            language: language || null,
            invoice_number: invoiceNumber || null,
        });
    };

    window.trackReservationClickGeneratePdf = function (language, invoiceNumber) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Generate PDF', {
            language: language || null,
            invoice_number: invoiceNumber || null,
        });
    };

    window.trackReservationClickSendInvoice = function (searchKeyword, invoiceNumber) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Send Invoice', {
            search_keyword: searchKeyword || null,
            invoice_number: invoiceNumber || null,
        });
    };

    window.trackReservationClickEdit = function (invoiceNumber) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Edit', {
            invoice_number: invoiceNumber || null,
        });
    };

    window.trackReservationSelectChargesType = function (chargesType) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Select Charges Type', {
            charges_type: chargesType || null,
        });
    };

    window.trackReservationClickAddAdditionalCharges = function (reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Add Additional Charges', {
            reservation_id: reservationId || null,
        });
    };

    window.trackReservationRemoveAdditionalCharges = function (reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Remove Additional Charges', {
            reservation_id: reservationId || null,
        });
    };

    window.trackReservationClickUpdateReservation = function (source, reservationId, tableName) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Update Reservation', {
            source: source || null,
            reservation_id: reservationId || null,
            table_name: tableName || null,
        });
    };

    window.trackReservationClickConfirmUpdateReservation = function (formData) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Confirm Update Reservation', formData || {});
    };

    // Damage alert update confirmation (reservation show report submit flow)
    window.trackReservationClickConfirmUpdateDamageAlert = function (formData) {
        if (!window.amplitudeTracker) return;

        const payload = { ...(formData || {}) };
        // Do not send tokens / secrets into analytics.
        if ('_token' in payload) delete payload._token;

        // Ensure consistent property name.
        if (payload.reservation_id == null && payload.reservationId != null) {
            payload.reservation_id = payload.reservationId;
        }

        window.amplitudeTracker._safeTrack(
            'Reservations - Click Confirm Update Damage Alert',
            payload
        );
    };

    // Damage Alert (reservation show detail) edit click tracking
    window.trackReservationClickEditDamageAlert = function (reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack(
            'Reservations - Click Edit Damage Alert',
            {
                source:
                    reservationId != null && String(reservationId).trim() !== ''
                        ? String(reservationId).trim()
                        : null,
                reservation_id:
                    reservationId != null && String(reservationId).trim() !== ''
                        ? String(reservationId).trim()
                        : null,
            }
        );
    };

    window.trackReservationClickUpdateDamageAlertSuccess = function (formData) {
        if (!window.amplitudeTracker) return;

        const payload = { ...(formData || {}) };
        if ('_token' in payload) delete payload._token;

        if (payload.reservation_id == null && payload.reservationId != null) {
            payload.reservation_id = payload.reservationId;
        }

        window.amplitudeTracker._safeTrack(
            'Reservations - Click Update Damage Alert Success',
            payload
        );
        window.amplitudeTracker.flush?.();
    };

    window.trackReservationClickUpdateDamageAlertFail = function (formData, error = null, errorCode = null) {
        if (!window.amplitudeTracker) return;

        const payload = { ...(formData || {}) };
        if ('_token' in payload) delete payload._token;

        if (payload.reservation_id == null && payload.reservationId != null) {
            payload.reservation_id = payload.reservationId;
        }

        window.amplitudeTracker._safeTrack(
            'Reservations - Click Update Damage Alert Fail',
            {
                ...payload,
                error_message: error ?? null,
                error_code: errorCode ?? null
            }
        );
        window.amplitudeTracker.flush?.();
    };

    window.trackReservationClickOKPasswordConfirmation = function (reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click OK Password Confirmation', {
            reservation_id: reservationId || null,
        });
    };

    window.trackReservationUpdateReservationSuccess = function (reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Update Reservation Success', {
            reservation_id: reservationId || null,
        });
    };

    window.trackReservationUpdateReservationFail = function (reservationId, errorCode = null, errorMessage = null) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Update Reservation Fail', {
            reservation_id: reservationId || null,
            error_code: errorCode || null,
            error_message: errorMessage || null,
        });
    };

    window.trackReservationClickCancelUpdateReservation = function () {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Cancel Update Reservation', {});
    };

    window.trackReservationClickArchivedReservation = function (tableName, source, reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Archived Reservation', {
            table_name: tableName || null,
            source: source || null,
            reservation_id: reservationId || null,
        });
    };

    window.trackReservationClickConfirmArchivedReservation = function (reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Confirm Archived Reservation', {
            reservation_id: reservationId || null,
        });
    };

    window.trackReservationClickCancelArchivedReservation = function (reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Cancel Archived Reservation', {
            reservation_id: reservationId || null,
        });
    };

    window.trackReservationArchivedReservationSuccess = function (reservationId) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Archived Reservation Success', {
            reservation_id: reservationId || null,
        });
    };

    window.trackReservationArchivedReservationFail = function (reservationId, errorMessage = null) {
        if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Archived Reservation Fail', {
            reservation_id: reservationId || null,
            error_message: errorMessage || null,
        });
    };

    /* ================= DELETE RESULT EVENTS ================ */
    /** Treats numeric 0 as valid; rejects only null/undefined/blank string. */
    function _missingGlobalDeleteEntity(entity, entityId) {
        if (entity == null || String(entity).trim() === '') return true;
        if (entityId === undefined || entityId === null) return true;
        if (typeof entityId === 'string' && entityId.trim() === '') return true;
        return false;
    }

    /** If entity is missing but we have an id, use `unknown` so cancel/confirm still emit (legacy SWAL + DT rows). */
    function _normalizeGlobalDeleteEntity(entity, entityId) {
        let e =
            entity != null && String(entity).trim() !== ''
                ? String(entity).trim().toLowerCase()
                : '';
        if (!e && entityId != null && String(entityId).trim() !== '') {
            e = 'unknown';
        }
        return e;
    }

    function _isExchangeRateEntity(entity) {
        const e = String(entity || '').toLowerCase();
        return e === 'exchange_rate' || e === 'exchangerate';
    }

    function _isAcrissEntity(entity) {
        return String(entity || '').toLowerCase() === 'acriss';
    }

    function _isRateBookEntity(entity) {
        return String(entity || '').toLowerCase() === 'rate';
    }

    /** Rate Book (soft delete only) — entity is `rate` from Rate Management. */
    function _isRateBookSoftDelete(entity, actionType) {
        return _isRateBookEntity(entity) && actionType === 'soft_delete';
    }

    function _isRateBookPermanentDelete(entity, actionType) {
        return _isRateBookEntity(entity) && actionType === 'permanent_delete';
    }

    function _isRateBookStatusAction(entity, actionType) {
        return (
            _isRateBookEntity(entity) &&
            (actionType === 'activate' || actionType === 'deactivate')
        );
    }

    function _rateBookRateIdProps(entityId, source = 'rate_book_list') {
        const rid =
            entityId != null && String(entityId).trim() !== ''
                ? String(entityId).trim()
                : null;
        return {
            source: source,
            entity: 'rate',
            rate_id: rid,
            entity_id: rid
        };
    }

    function _rateBookDeleteProps(entityId, actionType) {
        return {
            ..._rateBookRateIdProps(entityId, 'rate_book_list'),
            action_type: actionType
        };
    }

    function _acrissEventProps(entityId, actionType, acrissCode = null) {
        const code =
            acrissCode != null && String(acrissCode).trim() !== ''
                ? String(acrissCode).trim()
                : null;
        return {
            source: 'acriss_list',
            entity: 'acriss',
            acriss_id: entityId != null ? String(entityId) : null,
            entity_id: entityId != null ? String(entityId) : null,
            acriss_code: code,
            action_type: actionType
        };
    }

    function _acrissRestoreProps(entityId, restoreType, acrissCode = null) {
        const code =
            acrissCode != null && String(acrissCode).trim() !== ''
                ? String(acrissCode).trim()
                : null;
        return {
            source: 'acriss_deleted_list',
            entity: 'acriss',
            acriss_id: entityId != null ? String(entityId) : null,
            entity_id: entityId != null ? String(entityId) : null,
            acriss_code: code,
            restore_type: restoreType
        };
    }

    function _reservationIdProps(entity, entityId) {
        const e = String(entity || '').toLowerCase();
        if (!e.includes('reservation')) {
            return {};
        }
        return {
            reservation_id:
                entityId != null && String(entityId).trim() !== ''
                    ? String(entityId).trim()
                    : null
        };
    }

    function _exchangeRateEventProps(entityId, actionType) {
        return {
            source: 'exchange_rate_list',
            entity: 'exchange_rate',
            exchange_rate_id: entityId != null ? String(entityId) : null,
            entity_id: entityId != null ? String(entityId) : null,
            action_type: actionType
        };
    }

    function _promotionDeleteProps(entityId, actionType) {
        const source =
            actionType === 'permanent_delete'
                ? 'promotion_deleted_list'
                : 'promotion_list';
        return {
            source,
            entity: 'promotion',
            entity_id: entityId != null ? String(entityId) : null,
            action_type: actionType,
        };
    }

    function _isPromotionEntity(entity) {
        return String(entity || '').trim().toLowerCase() === 'promotion';
    }

    window.trackGlobalDeleteClick = function (
        entity,
        entityId,
        actionType = 'soft_delete',
        ctx = {}
    ) {
        if (!window.amplitudeTracker) return;
        
        entity = _normalizeGlobalDeleteEntity(entity, entityId);
        if (_missingGlobalDeleteEntity(entity, entityId)) {
            console.warn('[Amplitude] Missing entity or entityId for delete click');
            return;
        }

        const acrissCodeFromCtx =
            ctx && typeof ctx === 'object' && ctx.acriss_code != null
                ? ctx.acriss_code
                : null;

        if (_isExchangeRateEntity(entity)) {
            const eventName =
                actionType === 'permanent_delete'
                    ? 'Rates - Click Delete Permanently Exchange Rate'
                    : 'Rates - Click Delete Exchange Rate';
            window.amplitudeTracker._safeTrack(eventName, {
                ..._exchangeRateEventProps(entityId, actionType),
                _ts: Date.now()
            });
            return;
        }

        if (_isRateBookPermanentDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Delete Permanently Rate Book',
                {
                    ..._rateBookRateIdProps(
                        entityId,
                        'rate_book_deleted_list'
                    ),
                    action_type: actionType,
                    _ts: Date.now()
                }
            );
            return;
        }

        if (_isRateBookSoftDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack('Rates - Click Delete Rate Book', {
                ..._rateBookDeleteProps(entityId, actionType),
                _ts: Date.now()
            });
            return;
        }

        if (_isRateBookStatusAction(entity, actionType)) {
            const eventName =
                actionType === 'activate'
                    ? 'Rates - Click Activate Rate Book'
                    : 'Rates - Click Deactivate Rate Book';
            window.amplitudeTracker._safeTrack(eventName, {
                ..._rateBookRateIdProps(entityId, 'rate_book_list'),
                _ts: Date.now()
            });
            return;
        }

        if (_isAcrissEntity(entity)) {
            const eventName =
                actionType === 'permanent_delete'
                    ? 'Rates - Click Delete Permanently Acriss'
                    : 'Rates - Click Delete Acriss';
            window.amplitudeTracker._safeTrack(eventName, {
                ..._acrissEventProps(entityId, actionType, acrissCodeFromCtx),
                _ts: Date.now()
            });
            return;
        }

        const deleteLabel = actionType === 'permanent_delete' 
            ? CONFIG.ACTION_LABELS.delete.permanent 
            : CONFIG.ACTION_LABELS.delete.soft;

        const tracker = window.amplitudeTracker._trackAction('delete', entity, entityId, {
            actionLabel: deleteLabel,
            extraProps: { action_type: actionType, _ts: Date.now(), ..._reservationIdProps(entity, entityId) }
        });

        tracker.click();
    };

    window.trackGlobalDeleteSuccess = function (
        entity,
        entityId,
        actionType = 'soft_delete',
        ctx = {}
    ) {
        if (!window.amplitudeTracker) return;
        
        entity = _normalizeGlobalDeleteEntity(entity, entityId);
        if (_missingGlobalDeleteEntity(entity, entityId)) {
            console.warn('[Amplitude] Missing entity or entityId for delete success');
            return;
        }

        const acrissCodeFromCtx =
            ctx && typeof ctx === 'object' && ctx.acriss_code != null
                ? ctx.acriss_code
                : null;

        if (_isExchangeRateEntity(entity)) {
            const props = _exchangeRateEventProps(entityId, actionType);
            if (actionType === 'permanent_delete') {
                window.amplitudeTracker._safeTrack(
                    'Rates - Confirm Delete Permanently Exchange Rate Success',
                    props
                );
            } else {
                window.amplitudeTracker._safeTrack(
                    'Rates - Delete Exchange Rate Success',
                    props
                );
            }
            window.amplitudeTracker.flush?.();
            return;
        }

        if (_isRateBookPermanentDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Confirm Delete Permanently Rate Book Success',
                {
                    ..._rateBookRateIdProps(
                        entityId,
                        'rate_book_deleted_list'
                    ),
                    action_type: actionType
                }
            );
            window.amplitudeTracker.flush?.();
            return;
        }

        if (_isRateBookSoftDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack('Rates - Delete Rate Book Success', {
                ..._rateBookDeleteProps(entityId, actionType)
            });
            window.amplitudeTracker.flush?.();
            return;
        }

        if (_isAcrissEntity(entity)) {
            const props = _acrissEventProps(entityId, actionType, acrissCodeFromCtx);
            if (actionType === 'permanent_delete') {
                window.amplitudeTracker._safeTrack(
                    'Rates - Confirm Delete Permanently Acriss Success',
                    props
                );
            } else {
                window.amplitudeTracker._safeTrack(
                    'Rates - Delete Acriss Success',
                    props
                );
            }
            window.amplitudeTracker.flush?.();
            return;
        }

        const deleteLabel = actionType === 'permanent_delete' 
            ? 'Delete Permanently' 
            : 'Delete';

        const tracker = window.amplitudeTracker._trackAction('delete', entity, entityId, {
            successLabel: `Confirm ${deleteLabel}`,
            extraProps: { action_type: actionType, ..._reservationIdProps(entity, entityId) }
        });

        tracker.success();
    };

    window.trackGlobalDeleteFail = function (
        entity,
        entityId,
        actionType = 'soft_delete',
        error_code = null,
        error_message = null,
        ctx = {}
    ) {
        if (!window.amplitudeTracker) return;
        
        entity = _normalizeGlobalDeleteEntity(entity, entityId);
        if (_missingGlobalDeleteEntity(entity, entityId)) {
            console.warn('[Amplitude] Missing entity or entityId for delete fail');
            return;
        }

        const acrissCodeFromCtx =
            ctx && typeof ctx === 'object' && ctx.acriss_code != null
                ? ctx.acriss_code
                : null;

        if (_isExchangeRateEntity(entity)) {
            const props = {
                ..._exchangeRateEventProps(entityId, actionType),
                error_code: error_code ?? null,
                error_message: error_message ?? null
            };
            if (actionType === 'permanent_delete') {
                window.amplitudeTracker._safeTrack(
                    'Rates - Confirm Delete Permanently Exchange Rate Fail',
                    props
                );
            } else {
                window.amplitudeTracker._safeTrack(
                    'Rates - Delete Exchange Rate Fail',
                    props
                );
            }
            window.amplitudeTracker.flush?.();
            return;
        }

        if (_isRateBookPermanentDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Confirm Delete Permanently Rate Book Fail',
                {
                    ..._rateBookRateIdProps(
                        entityId,
                        'rate_book_deleted_list'
                    ),
                    action_type: actionType,
                    error_code: error_code ?? null,
                    error_message: error_message ?? null
                }
            );
            window.amplitudeTracker.flush?.();
            return;
        }

        if (_isRateBookSoftDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack('Rates - Delete Rate Book Fail', {
                ..._rateBookDeleteProps(entityId, actionType),
                error_code: error_code ?? null,
                error_message: error_message ?? null
            });
            window.amplitudeTracker.flush?.();
            return;
        }

        if (_isAcrissEntity(entity)) {
            const props = {
                ..._acrissEventProps(entityId, actionType, acrissCodeFromCtx),
                error_code: error_code ?? null,
                error_message: error_message ?? null
            };
            if (actionType === 'permanent_delete') {
                window.amplitudeTracker._safeTrack(
                    'Rates - Confirm Delete Permanently Acriss Fail',
                    props
                );
            } else {
                window.amplitudeTracker._safeTrack(
                    'Rates - Delete Acriss Fail',
                    props
                );
            }
            window.amplitudeTracker.flush?.();
            return;
        }

        const deleteLabel = actionType === 'permanent_delete'
            ? 'Delete Permanently'
            : 'Delete';

        const tracker = window.amplitudeTracker._trackAction('delete', entity, entityId, {
            failLabel: `Confirm ${deleteLabel}`,
            extraProps: { action_type: actionType, ..._reservationIdProps(entity, entityId) }
        });

        tracker.fail({ code: error_code, message: error_message });
    };

    window.trackGlobalDeleteConfirm = function (
        entity,
        entityId,
        actionType = 'soft_delete',
        ctx = {}
    ) {
        if (!window.amplitudeTracker) return;
        
        entity = _normalizeGlobalDeleteEntity(entity, entityId);
        if (_missingGlobalDeleteEntity(entity, entityId)) {
            console.warn('[Amplitude] Missing entity or entityId for delete confirm');
            return;
        }

        const acrissCodeFromCtx =
            ctx && typeof ctx === 'object' && ctx.acriss_code != null
                ? ctx.acriss_code
                : null;

        if (_isExchangeRateEntity(entity)) {
            const eventName =
                actionType === 'permanent_delete'
                    ? 'Rates - Click Confirm Delete Permanently Exchange Rate'
                    : 'Rates - Click Confirm Delete Exchange Rate';
            window.amplitudeTracker._safeTrack(
                eventName,
                _exchangeRateEventProps(entityId, actionType)
            );
            return;
        }

        if (_isRateBookPermanentDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Confirm Delete Permanently Rate Book',
                {
                    ..._rateBookRateIdProps(
                        entityId,
                        'rate_book_deleted_list'
                    ),
                    action_type: actionType
                }
            );
            return;
        }

        if (_isRateBookSoftDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Confirm Delete Rate Book',
                _rateBookDeleteProps(entityId, actionType)
            );
            return;
        }

        if (_isRateBookStatusAction(entity, actionType)) {
            const eventName =
                actionType === 'activate'
                    ? 'Rates - Click Confirm Activate Rate Book'
                    : 'Rates - Click Confirm Deactivate Rate Book';
            window.amplitudeTracker._safeTrack(eventName, {
                ..._rateBookRateIdProps(entityId, 'rate_book_list')
            });
            return;
        }

        if (_isAcrissEntity(entity)) {
            const eventName =
                actionType === 'permanent_delete'
                    ? 'Rates - Click Confirm Delete Permanently Acriss'
                    : 'Rates - Click Confirm Delete Acriss';
            window.amplitudeTracker._safeTrack(
                eventName,
                _acrissEventProps(entityId, actionType, acrissCodeFromCtx)
            );
            return;
        }

        if (_isPromotionEntity(entity)) {
            const isPermanent =
                String(actionType || '').toLowerCase() === 'permanent_delete';
            const confirmEventName = isPermanent
                ? 'Promotion - Click Confirm Delete Permanently Promotion Code'
                : 'Promotion - Click Confirm Delete Promotion Code';
            window.amplitudeTracker._safeTrack(
                confirmEventName,
                _promotionDeleteProps(entityId, actionType)
            );
            return;
        }

        const deleteLabel = actionType === 'permanent_delete'
            ? CONFIG.ACTION_LABELS.delete.permanent
            : CONFIG.ACTION_LABELS.delete.soft;

        const tracker = window.amplitudeTracker._trackAction('delete', entity, entityId, {
            actionLabel: deleteLabel,
            extraProps: { action_type: actionType, ..._reservationIdProps(entity, entityId) }
        });

        tracker.confirm();
    };

    window.trackGlobalDeleteCancel = function (
        entity,
        entityId,
        actionType = 'soft_delete',
        ctx = {}
    ) {
        if (!window.amplitudeTracker) return;
        
        entity = _normalizeGlobalDeleteEntity(entity, entityId);
        if (_missingGlobalDeleteEntity(entity, entityId)) {
            console.warn('[Amplitude] Missing entity or entityId for delete cancel');
            return;
        }

        const acrissCodeFromCtx =
            ctx && typeof ctx === 'object' && ctx.acriss_code != null
                ? ctx.acriss_code
                : null;

        if (_isExchangeRateEntity(entity)) {
            const eventName =
                actionType === 'permanent_delete'
                    ? 'Rates - Click Cancel Delete Permanently Exchange Rate'
                    : 'Rates - Click Cancel Delete Exchange Rate';
            window.amplitudeTracker._safeTrack(
                eventName,
                _exchangeRateEventProps(entityId, actionType)
            );
            return;
        }

        if (_isRateBookPermanentDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Cancel Delete Permanently Rate Book',
                {
                    ..._rateBookRateIdProps(
                        entityId,
                        'rate_book_deleted_list'
                    ),
                    action_type: actionType
                }
            );
            return;
        }

        if (_isRateBookSoftDelete(entity, actionType)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Cancel Delete Rate Book',
                _rateBookDeleteProps(entityId, actionType)
            );
            return;
        }

        if (_isRateBookStatusAction(entity, actionType)) {
            const eventName =
                actionType === 'activate'
                    ? 'Rates - Click Cancel Activate Rate Book'
                    : 'Rates - Click Cancel Deactivate Rate Book';
            window.amplitudeTracker._safeTrack(eventName, {
                ..._rateBookRateIdProps(entityId, 'rate_book_list')
            });
            return;
        }

        if (_isAcrissEntity(entity)) {
            const eventName =
                actionType === 'permanent_delete'
                    ? 'Rates - Click Cancel Delete Permanently Acriss'
                    : 'Rates - Click Cancel Delete Acriss';
            window.amplitudeTracker._safeTrack(
                eventName,
                _acrissEventProps(entityId, actionType, acrissCodeFromCtx)
            );
            return;
        }

        if (_isPromotionEntity(entity)) {
            const isPermanent =
                String(actionType || '').toLowerCase() === 'permanent_delete';
            const cancelEventName = isPermanent
                ? 'Promotion - Click Cancel Delete Permanently Promotion Code'
                : 'Promotion - Click Cancel Delete Promotion Code';
            window.amplitudeTracker._safeTrack(
                cancelEventName,
                _promotionDeleteProps(entityId, actionType)
            );
            window.amplitudeTracker.flush?.();
            return;
        }

        if (String(entity || '').toLowerCase() === 'notification') {
            if (typeof window.trackGlobalNotificationDeleteCancel === 'function') {
                window.trackGlobalNotificationDeleteCancel(entityId, actionType);
            }
            return;
        }

        const deleteLabel = actionType === 'permanent_delete'
            ? CONFIG.ACTION_LABELS.delete.permanent
            : CONFIG.ACTION_LABELS.delete.soft;

        const tracker = window.amplitudeTracker._trackAction('delete', entity, entityId, {
            actionLabel: deleteLabel,
            extraProps: { action_type: actionType, ..._reservationIdProps(entity, entityId) }
        });

        tracker.cancel();
        window.amplitudeTracker.flush?.();
    };

    /*================== SweetAlert Password ================== */
    /* ================= GLOBAL SYNC TRACKING ================= */
    const _lastSyncClick = new Map();

    window.trackGlobalSyncClick = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for sync click');
            return;
        }

        // Deduplicate rapid double-clicks per entity/id
        const key = `${entity}:${entityId}`;
        const now = Date.now();
        const last = _lastSyncClick.get(key);
        if (last && now - last < 500) {
            return;
        }
        _lastSyncClick.set(key, now);

        const tracker = window.amplitudeTracker._trackAction('sync', entity, entityId, {
            actionLabel: CONFIG.ACTION_LABELS.sync,
            extraProps: { status: 'active' }
        });

        tracker.click();
    };

    window.trackGlobalSyncConfirm = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for sync confirm');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('sync', entity, entityId, {
            actionLabel: CONFIG.ACTION_LABELS.sync,
            // Let _trackAction prepend "Confirm " once
            confirmLabel: CONFIG.ACTION_LABELS.sync,
            extraProps: { status: 'active' }
        });

        tracker.confirm();
    };

    window.trackGlobalSyncCancel = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for sync cancel');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('sync', entity, entityId, {
            actionLabel: CONFIG.ACTION_LABELS.sync,
            cancelLabel: CONFIG.ACTION_LABELS.sync,
            extraProps: { status: 'active' }
        });

        tracker.cancel();
    };

    window.trackGlobalSyncSuccess = function (entity, entityId) {
        if (!window.amplitudeTracker) return false;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for sync success');
            return false;
        }

        const tracker = window.amplitudeTracker._trackAction('sync', entity, entityId, {
            successLabel: `Click ${CONFIG.ACTION_LABELS.sync}`,
            extraProps: { status: 'success' }
        });

        return tracker.success();
    };

    window.trackGlobalSyncFail = function (entity, entityId, error = null) {
        if (!window.amplitudeTracker) return false;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for sync fail');
            return false;
        }

        const tracker = window.amplitudeTracker._trackAction('sync', entity, entityId, {
            failLabel: `Click ${CONFIG.ACTION_LABELS.sync}`,
            extraProps: { status: 'fail' }
        });

        return tracker.fail(error);
    };

    /* ================= VEHICLE ARCHIVE (password flow) ================= */
    const _lastVehicleArchiveClick = new Map();

    window.trackGlobalVehicleArchiveClick = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for vehicle archive click');
            return;
        }

        const key = `${entity}:${entityId}`;
        const now = Date.now();
        const last = _lastVehicleArchiveClick.get(key);
        if (last && now - last < 500) {
            return;
        }
        _lastVehicleArchiveClick.set(key, now);

        const tracker = window.amplitudeTracker._trackAction('archive', entity, entityId, {
            actionLabel: 'Archive',
            extraProps: { _ts: Date.now() }
        });

        tracker.click();
    };

    window.trackGlobalVehicleArchiveConfirm = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for vehicle archive confirm');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('archive', entity, entityId, {
            actionLabel: 'Archive',
            confirmLabel: 'Archive',
            extraProps: {}
        });

        tracker.confirm();
    };

    window.trackGlobalVehicleArchiveCancel = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for vehicle archive cancel');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('archive', entity, entityId, {
            actionLabel: 'Archive',
            cancelLabel: 'Archive',
            extraProps: {}
        });

        tracker.cancel();
    };

    window.trackGlobalVehicleArchiveSuccess = function (entityId) {
        if (!window.amplitudeTracker) return;
        if (!entityId) {
            console.warn('[Amplitude] Missing entityId for vehicle archive success');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('archive', 'vehicle', entityId, {
            successLabel: 'Confirm Archive'
        });

        tracker.success();
    };

    window.trackGlobalVehicleArchiveFail = function (entityId, error = null) {
        if (!window.amplitudeTracker) return;
        if (!entityId) {
            console.warn('[Amplitude] Missing entityId for vehicle archive fail');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('archive', 'vehicle', entityId, {
            failLabel: 'Confirm Archive'
        });

        tracker.fail(error);
    };

    /* ================= SAVE TO OUR VEHICLE (form_restore + swal, type=store) ================= */
    window.trackGlobalSaveToOurVehicleClick = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for save to our vehicle click');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('save_our_vehicle', entity, entityId, {
            actionLabel: 'Save to Our Vehicle',
            extraProps: { _ts: Date.now() }
        });

        tracker.click();
    };

    window.trackGlobalSaveToOurVehicleConfirm = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for save to our vehicle confirm');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('save_our_vehicle', entity, entityId, {
            actionLabel: 'Save to Our Vehicle',
            confirmLabel: 'Save to Our Vehicle',
            extraProps: {}
        });

        tracker.confirm();
    };

    window.trackGlobalSaveToOurVehicleCancel = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for save to our vehicle cancel');
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('save_our_vehicle', entity, entityId, {
            actionLabel: 'Save to Our Vehicle',
            cancelLabel: 'Save to Our Vehicle',
            extraProps: {}
        });

        tracker.cancel();
    };


/* ================= REDIRECT FLASH HANDLER ================= */

document.addEventListener('DOMContentLoaded', function () {
    if (!window.sessionFlash) return;

    function tryTrackSyncFlash() {
        const flash = window.sessionFlash;
        let tracked = false;

        if (flash.sync_success) {
            tracked = trackGlobalSyncSuccess(
                flash.entity,
                flash.entity_id
            );
        }

        if (flash.sync_fail) {
            tracked = trackGlobalSyncFail(
                flash.entity,
                flash.entity_id,
                flash.error
            );
        }

        // Clear ONLY after Amplitude is ready & event sent
        if (tracked) {
            window.sessionFlash = null;
        }

        return tracked;
    }

    // Try immediately
    if (tryTrackSyncFlash()) return;

    // Retry until Amplitude is ready
    const interval = setInterval(() => {
        if (tryTrackSyncFlash()) {
            clearInterval(interval);
        }
    }, 300);
});

    /* ================= RESTORE RESULT EVENTS ================= */

    function _exchangeRateRestoreProps(entityId, restoreType) {
        return {
            source: 'exchange_rate_deleted_list',
            entity: 'exchange_rate',
            exchange_rate_id: entityId != null ? String(entityId) : null,
            entity_id: entityId != null ? String(entityId) : null,
            restore_type: restoreType
        };
    }

    window.trackGlobalRestoreSuccess = function (entity, entityId) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for restore success');
            return;
        }

        if (_isRateBookEntity(entity)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Confirm Restore Deleted Rate Book Success',
                {
                    ..._rateBookRateIdProps(entityId, 'rate_book_deleted_list'),
                    restore_type: 'deleted'
                }
            );
            window.amplitudeTracker.flush?.();
            return;
        }

        if (_isPromotionEntity(entity)) {
            const tracked = window.amplitudeTracker._safeTrack(
                'Promotion - Confirm Restore Deleted Promotion Code Success',
                {
                    entity: 'promotion',
                    entity_id: entityId,
                    source: 'promotion_deleted_list',
                    restore_type: 'deleted',
                }
            );
            if (tracked) window.amplitudeTracker.flush?.();
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('restore', entity, entityId, {
            successLabel: 'Confirm Restore Deleted',
            extraProps: _reservationIdProps(entity, entityId)
        });

        tracker.success();
    };

    window.trackGlobalRestoreFail = function (
        entity,
        entityId,
        error = null,
        errorCode = null
    ) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for restore fail');
            return;
        }

        if (_isRateBookEntity(entity)) {
            window.amplitudeTracker._safeTrack(
                'Rates - Confirm Restore Deleted Rate Book Fail',
                {
                    ..._rateBookRateIdProps(entityId, 'rate_book_deleted_list'),
                    restore_type: 'deleted',
                    error_code: errorCode ?? null,
                    error_message: error ?? null
                }
            );
            window.amplitudeTracker.flush?.();
            return;
        }

        if (_isPromotionEntity(entity)) {
            const tracked = window.amplitudeTracker._safeTrack(
                'Promotion - Confirm Restore Deleted Promotion Code Fail',
                {
                    entity: 'promotion',
                    entity_id: entityId,
                    source: 'promotion_deleted_list',
                    restore_type: 'deleted',
                    error_code: errorCode ?? null,
                    error_message: error ?? null,
                }
            );
            if (tracked) window.amplitudeTracker.flush?.();
            return;
        }

        const tracker = window.amplitudeTracker._trackAction('restore', entity, entityId, {
            failLabel: 'Confirm Restore Deleted',
            extraProps: _reservationIdProps(entity, entityId)
        });

        tracker.fail(error);
    };

    window.trackGlobalRestoreClick = function (
        entity,
        entityId,
        restoreType = 'deleted',
        ctx = {}
    ) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for restore click');
            return;
        }

        const acrissCodeFromCtx =
            ctx && typeof ctx === 'object' && ctx.acriss_code != null
                ? ctx.acriss_code
                : null;

        if (_isExchangeRateEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Restore Deleted Exchange Rate',
                { ..._exchangeRateRestoreProps(entityId, restoreType), _ts: Date.now() }
            );
            return;
        }

        if (_isRateBookEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack('Rates - Click Restore Deleted Rate Book', {
                ..._rateBookRateIdProps(entityId, 'rate_book_deleted_list'),
                restore_type: restoreType,
                _ts: Date.now()
            });
            return;
        }

        if (_isAcrissEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Restore Deleted Acriss',
                {
                    ..._acrissRestoreProps(entityId, restoreType, acrissCodeFromCtx),
                    _ts: Date.now()
                }
            );
            return;
        }

        const label = restoreType === 'sync'
            ? CONFIG.ACTION_LABELS.restore.sync
            : CONFIG.ACTION_LABELS.restore.deleted;

        const tracker = window.amplitudeTracker._trackAction('restore', entity, entityId, {
            actionLabel: label,
            extraProps: { restore_type: restoreType, _ts: Date.now(), ..._reservationIdProps(entity, entityId) }
        });

        tracker.click();
    };

    window.trackGlobalRestoreConfirm = function (
        entity,
        entityId,
        restoreType = 'deleted',
        ctx = {}
    ) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for restore confirm');
            return;
        }

        const acrissCodeFromCtx =
            ctx && typeof ctx === 'object' && ctx.acriss_code != null
                ? ctx.acriss_code
                : null;

        if (_isExchangeRateEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Confirm Restore Deleted Exchange Rate',
                _exchangeRateRestoreProps(entityId, restoreType)
            );
            return;
        }

        if (_isRateBookEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Confirm Restore Deleted Rate Book',
                {
                    ..._rateBookRateIdProps(entityId, 'rate_book_deleted_list'),
                    restore_type: restoreType
                }
            );
            return;
        }

        if (_isAcrissEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Confirm Restore Deleted Acriss',
                _acrissRestoreProps(entityId, restoreType, acrissCodeFromCtx)
            );
            return;
        }

        const label = restoreType === 'sync' 
            ? CONFIG.ACTION_LABELS.restore.sync 
            : CONFIG.ACTION_LABELS.restore.deleted;

        const tracker = window.amplitudeTracker._trackAction('restore', entity, entityId, {
            actionLabel: label,
            confirmLabel: label,
            extraProps: { restore_type: restoreType, ..._reservationIdProps(entity, entityId) }
        });

        tracker.confirm();
    };

    window.trackGlobalRestoreCancel = function (
        entity,
        entityId,
        restoreType = 'deleted',
        ctx = {}
    ) {
        if (!window.amplitudeTracker) return;
        if (!entity || !entityId) {
            console.warn('[Amplitude] Missing entity or entityId for restore cancel');
            return;
        }

        const acrissCodeFromCtx =
            ctx && typeof ctx === 'object' && ctx.acriss_code != null
                ? ctx.acriss_code
                : null;

        if (_isExchangeRateEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Cancel Restore Deleted Exchange Rate',
                _exchangeRateRestoreProps(entityId, restoreType)
            );
            return;
        }

        if (_isRateBookEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Cancel Restore Deleted Rate Book',
                {
                    ..._rateBookRateIdProps(entityId, 'rate_book_deleted_list'),
                    restore_type: restoreType
                }
            );
            return;
        }

        if (_isAcrissEntity(entity) && restoreType !== 'sync') {
            window.amplitudeTracker._safeTrack(
                'Rates - Click Cancel Restore Deleted Acriss',
                _acrissRestoreProps(entityId, restoreType, acrissCodeFromCtx)
            );
            return;
        }

        const label = restoreType === 'sync' 
            ? CONFIG.ACTION_LABELS.restore.sync 
            : CONFIG.ACTION_LABELS.restore.deleted;

        const tracker = window.amplitudeTracker._trackAction('restore', entity, entityId, {
            actionLabel: label,
            cancelLabel: label,
            extraProps: { restore_type: restoreType, ..._reservationIdProps(entity, entityId) }
        });

        tracker.cancel();
    };

    document.addEventListener('click', function (e) {
        const el = e.target.closest('[data-amplitude-action]');
        if (!el || !window.amplitudeTracker) return;

        // Skip actions handled by SweetAlert flows in `plugin/alert.js` (avoids duplicates)
        if (
            el.getAttribute('name') === 'form_confirm_password' ||
            el.getAttribute('name') === 'form_confirm' ||
            el.getAttribute('name') === 'form_permanently_delete' ||
            el.getAttribute('name') === 'form_delete' ||
            el.dataset.amplitudeAction === 'Synced'
        ) {
            return;
        }

        // Restore / save-to-our-vehicle use form_restore in alert.js → trackGlobalRestoreClick / trackGlobalSaveToOurVehicleClick
        if (el.getAttribute('name') === 'form_restore') {
            return;
        }

        // Skip header-specific actions (handled by first listener)
        const headerActions = ['Menu Toggle', 'Notification', 'Activities', 'Offers', 'Feeds', 'Close Tab'];
        if (headerActions.includes(el.dataset.amplitudeAction)) {
            return;
        }

        const ampEntity = el.dataset.amplitudeEntity || '';
        const rawAction = el.dataset.amplitudeAction || '';
        // Rate Book: avoid "Rates - Click Create Rates Rates" (action + menu both "Rates")
        if (
            ampEntity === 'rate' &&
            (rawAction === 'Create Rates' || rawAction === 'Create Rate Book')
        ) {
            window.amplitudeTracker._safeTrack('Rates - Click Create Rate Book', {
                entity: 'rate',
                source: 'rate_book_dropdown'
            });
            window.amplitudeTracker.flush?.();
            return;
        }
        if (
            ampEntity === 'rate' &&
            (rawAction === 'Edit' || rawAction === 'Update Rate Book')
        ) {
            const rid = el.dataset.amplitudeEntityId;
            window.amplitudeTracker._safeTrack('Rates - Click Update Rate Book', {
                entity: 'rate',
                rate_id: rid != null && String(rid).trim() !== '' ? String(rid).trim() : null,
                source: 'rate_book'
            });
            window.amplitudeTracker.flush?.();
            return;
        }

        const moduleName = getCurrentModule();
        const menuName   = getCurrentMenu();
        const normalizedModuleName =
            String(moduleName || '').toLowerCase() === 'reservation'
                ? 'Reservations'
                : moduleName;
        const normalizedMenuName =
            String(menuName || '').toLowerCase() === 'reservation'
                ? 'Reservations'
                : menuName;

        amplitudeTracker.track(
            `${normalizedModuleName} - Click ${el.dataset.amplitudeAction} ${normalizedMenuName}`,
            {
                entity: el.dataset.amplitudeEntity,
                entity_id: el.dataset.amplitudeEntityId,
                restore_type: el.dataset.amplitudeRestoreType || null,
                invoice_number: el.dataset.amplitudeInvoiceNumber || null,
                source: normalizedMenuName
            }
        );
    });



        // Modal save button (legacy)
        document.addEventListener('click', e => {
            if (e.target.matches('#save-enquiry-status')) {
                const enquiryId = document.getElementById('edit_enquiry_id')?.value;
                const status = document.querySelector('input[name="status"]:checked')?.value;
                
                if (enquiryId && status) {
                    this.inquiry.saveStatus(enquiryId, status);
                }
            }
        });
    }
}


/* ===============================
   GLOBAL CREATE TRACKING
================================ */
const moduleEventPropertyMap = {
    membership: ({ entity, mode, payload }) => ({
        source: `${entity}_${mode.toLowerCase()}`,
        entity,
        mode,
        module: 'membership',
        ...window.__FORM_CONFIRM_PAYLOAD__,
        ...payload, // allow overrides
    }),

    promotion: ({ entity, mode, payload }) => ({
        source: `${entity}_${mode.toLowerCase()}`,
        entity,
        mode,
        module: 'promotion',
        ...window.__FORM_CONFIRM_PAYLOAD__,
        ...payload, // allow overrides
    }),

    invoice: ({ entity, mode, payload }) => ({
        source: `${entity}_${mode.toLowerCase()}`,
        entity,
        mode,
        module: 'invoice',
        ...window.__FORM_CONFIRM_PAYLOAD__,
        ...payload, // allow overrides
    }),

    coupon: ({ entity, mode, payload }) => ({
        source: `${entity}_${mode.toLowerCase()}`,
        entity,
        mode,
        module: 'coupon',
        ...window.__FORM_CONFIRM_PAYLOAD__,
        ...payload, // allow overrides
    }),

    point: ({ entity, mode, payload }) => ({
        source: `${entity}_${mode.toLowerCase()}`,
        entity,
        mode,
        module: 'point',
        ...window.__FORM_CONFIRM_PAYLOAD__,
        ...payload, // allow overrides
    }),

    location: ({ entity, mode, payload }) => ({
        source: `${entity}_${mode.toLowerCase()}`,
        entity,
        mode,
        module: 'location',
        ...window.__FORM_CONFIRM_PAYLOAD__,
        ...payload, // allow overrides
    }),

    notification: ({ entity, mode, payload }) => ({
        source: `${entity}_${mode.toLowerCase()}`,
        entity,
        mode,
        module: 'notification',
        ...window.__FORM_CONFIRM_PAYLOAD__,
        ...payload,
    }),

    reservation: ({ entity, mode, payload }) => ({
        source: `${entity}_${mode.toLowerCase()}`,
        entity,
        mode,
        module: 'reservation',
        ...window.__FORM_CONFIRM_PAYLOAD__,
        ...payload, // allow overrides
    }),

    rates: ({ entity, mode, payload }) => {
        const base = {
            source: `${entity}_${mode.toLowerCase()}`,
            entity,
            mode,
            module: 'rates',
            ...(window.__FORM_CONFIRM_PAYLOAD__ || {}),
            ...payload
        };
        const el = String(entity || '').toLowerCase();
        const m = String(mode || '').toLowerCase();
        if (el === 'exchange_rate' && (m === 'create' || m === 'update')) {
            const code =
                payload.code != null && String(payload.code).trim() !== ''
                    ? String(payload.code).trim()
                    : null;
            let rateVal = null;
            if (payload.rate != null && String(payload.rate).trim() !== '') {
                const n = Number(String(payload.rate).trim());
                rateVal = Number.isFinite(n) ? n : String(payload.rate).trim();
            }
            return { ...base, code, rate: rateVal };
        }
        if (el === 'acriss' && (m === 'create' || m === 'update')) {
            const acriss_code =
                payload.acriss_code != null &&
                String(payload.acriss_code).trim() !== ''
                    ? String(payload.acriss_code).trim()
                    : null;
            return { ...base, acriss_code };
        }
        return base;
    }
};

/** Create Acriss modal — passes acriss_code into trackGlobalFormConfirm. */
window.trackGlobalAcrissFormConfirmCreate = function () {
    const modal = document.getElementById('acriss');
    const codeEl = modal
        ? modal.querySelector('input[name="code"]')
        : document.querySelector('#create-acriss input[name="code"]');
    window.trackGlobalFormConfirm('acriss', 'Create', {
        acriss_code:
            codeEl && String(codeEl.value).trim() !== ''
                ? String(codeEl.value).trim()
                : null
    });
};

/**
 * Edit Acriss row — `Rates - Click Update Acriss` with `acriss_code`.
 * Pass the anchor element: `trackGlobalAcrissClickUpdate(this)` (reads data-acriss-id / data-acriss-code),
 * or legacy `(acrissId, acrissCode)` strings.
 */
window.trackGlobalAcrissClickUpdate = function (acrissIdOrEl, acrissCodeMaybe) {
    try {
        if (!window.amplitudeTracker) return;

        let acrissId;
        let acrissCode;

        if (
            acrissIdOrEl &&
            typeof acrissIdOrEl === 'object' &&
            acrissIdOrEl.nodeType === 1 &&
            acrissIdOrEl.dataset
        ) {
            const ds = acrissIdOrEl.dataset;
            acrissId =
                ds.acrissId != null && String(ds.acrissId).trim() !== ''
                    ? String(ds.acrissId).trim()
                    : null;
            acrissCode =
                ds.acrissCode != null && String(ds.acrissCode).trim() !== ''
                    ? String(ds.acrissCode).trim()
                    : null;
        } else {
            acrissId =
                acrissIdOrEl != null && String(acrissIdOrEl).trim() !== ''
                    ? String(acrissIdOrEl).trim()
                    : null;
            acrissCode =
                acrissCodeMaybe != null &&
                String(acrissCodeMaybe).trim() !== ''
                    ? String(acrissCodeMaybe).trim()
                    : null;
        }

        window.amplitudeTracker._safeTrack('Rates - Click Update Acriss', {
            source: 'acriss_list',
            entity: 'acriss',
            acriss_id: acrissId,
            acriss_code: acrissCode
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalAcrissClickUpdate:', e);
    }
};

/** Edit Acriss modal submit — passes acriss_code into trackGlobalFormConfirm. */
window.trackGlobalAcrissFormConfirmUpdate = function (acrissId) {
    const modal = document.getElementById('editModel' + acrissId);
    const codeEl = modal && modal.querySelector('input[name="code"]');
    window.trackGlobalFormConfirm('acriss', 'Update', {
        acriss_code:
            codeEl && String(codeEl.value).trim() !== ''
                ? String(codeEl.value).trim()
                : null
    });
};

/** Create Exchange Rate modal — passes code + rate into trackGlobalFormConfirm. */
window.trackGlobalExchangeRateFormConfirmCreate = function () {
    const cc = document.getElementById('currency_code');
    const rt = document.getElementById('rate');
    window.trackGlobalFormConfirm('exchange_rate', 'Create', {
        code: cc && cc.value ? String(cc.value).trim() : null,
        rate: rt && String(rt.value).trim() !== '' ? rt.value : null
    });
};

/** Edit row — opens modal (replaces generic "Open Edit Exchange" click). */
window.trackGlobalExchangeRateClickUpdate = function (exchangeId) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('Rates - Click Update Exchange Rate', {
            source: 'exchange_rate_list',
            entity: 'exchange_rate',
            exchange_rate_id:
                exchangeId != null && exchangeId !== ''
                    ? String(exchangeId)
                    : null
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalExchangeRateClickUpdate:', e);
    }
};

/** Edit Exchange Rate modal submit — passes code + rate into trackGlobalFormConfirm. */
window.trackGlobalExchangeRateFormConfirmUpdate = function (exchangeId) {
    const modal = document.getElementById('editModel' + exchangeId);
    const cc = modal && modal.querySelector('input[name="currency_code"]');
    const rt = modal && modal.querySelector('input[name="rate"]');
    window.trackGlobalFormConfirm('exchange_rate', 'Update', {
        code: cc && cc.value ? String(cc.value).trim() : null,
        rate: rt && String(rt.value).trim() !== '' ? rt.value : null
    });
};

/** Collect Rate Book (#rate-create) field values for Amplitude (snake_case keys). */
window.collectRateBookFormPayload = function () {
    const form = document.getElementById('rate-create');
    if (!form) return {};

    const names = [
        'acriss_id',
        'demand',
        'hourly_2000_0559',
        'hourly_0600_0859',
        'hourly_0900_1659',
        'hourly_1700_1959',
        'days_1_6',
        'days_7_13',
        'days_14_20',
        'days_21_30',
        'status',
        'price_per_kilometers'
    ];

    const out = {};

    names.forEach((name) => {
        if (typeof $ !== 'undefined') {
            const $field = $(form).find(`[name="${name}"]`);
            if ($field.length && $field.data('select2')) {
                const v = $field.val();
                out[name] =
                    v != null && v !== '' && !(Array.isArray(v) && v.length === 0)
                        ? Array.isArray(v)
                            ? v.map(String)
                            : String(v)
                        : null;
                return;
            }
        }

        const el = form.querySelector(`[name="${name}"]`);
        if (!el) {
            out[name] = null;
            return;
        }
        if (el.type === 'radio') {
            const checked = form.querySelector(`[name="${name}"]:checked`);
            out[name] = checked ? String(checked.value) : null;
            return;
        }
        if (el.tagName === 'SELECT') {
            out[name] =
                el.value != null && String(el.value).trim() !== ''
                    ? String(el.value).trim()
                    : null;
            return;
        }
        out[name] =
            el.value != null && String(el.value).trim() !== ''
                ? String(el.value).trim()
                : null;
    });

    const ridAttr = form.getAttribute('data-rate-id');
    if (ridAttr != null && String(ridAttr).trim() !== '') {
        out.rate_id = String(ridAttr).trim();
    }

    return out;
};

/** Submit on Create Rate Book — all form fields as properties. */
window.trackGlobalRateBookFormConfirmCreate = function () {
    try {
        if (!window.amplitudeTracker) return;
        const fields =
            typeof window.collectRateBookFormPayload === 'function'
                ? window.collectRateBookFormPayload()
                : {};
        window.amplitudeTracker._safeTrack(
            'Rates - Click Confirm Create Rate Book',
            {
                entity: 'rate',
                source: 'rate_book_form',
                ...fields
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalRateBookFormConfirmCreate:', e);
    }
};

/** Submit on Update Rate Book — all form fields + rate_id as properties. */
window.trackGlobalRateBookFormConfirmUpdate = function () {
    try {
        if (!window.amplitudeTracker) return;
        const fields =
            typeof window.collectRateBookFormPayload === 'function'
                ? window.collectRateBookFormPayload()
                : {};
        window.amplitudeTracker._safeTrack(
            'Rates - Click Confirm Update Rate Book',
            {
                entity: 'rate',
                source: 'rate_book_form',
                ...fields
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalRateBookFormConfirmUpdate:', e);
    }
};

/** Rate Book form submit — Create / Update use full field payloads. */
window.trackGlobalRateBookFormConfirmSubmit = function (mode) {
    const m = String(mode || '').toLowerCase();
    if (m === 'create') {
        if (typeof window.trackGlobalRateBookFormConfirmCreate === 'function') {
            window.trackGlobalRateBookFormConfirmCreate();
        }
        return;
    }
    if (m === 'update') {
        if (typeof window.trackGlobalRateBookFormConfirmUpdate === 'function') {
            window.trackGlobalRateBookFormConfirmUpdate();
        }
        return;
    }
    if (typeof window.trackGlobalFormConfirm === 'function') {
        window.trackGlobalFormConfirm('rate', mode);
    }
};

// Universal tracker
window.trackGlobalFormConfirm = function (entity, mode, payload = {}) {
    try {
        if (!window.amplitudeTracker) {
            console.warn('[Amplitude] Tracker not initialized');
            return;
        }

        if (!entity || !mode) {
            console.warn('[Amplitude] Missing entity or mode for form confirm');
            return;
        }

        const moduleName = typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown'; // e.g., "Invoice"
        let menuName   = typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown';   // e.g., "Create"
        // Avoid polluting event names with "Unknown" when we can infer it from entity.
        const entityLower = String(entity).toLowerCase();
        if (menuName === 'Unknown' && (entityLower === 'customer' || entityLower === 'customers')) {
            menuName = 'Customers';
        }
        const moduleKey  = moduleName.toLowerCase();

        // Determine how to build properties
        const buildProperties =
            moduleEventPropertyMap[moduleKey] ||
            (({ entity, mode, payload }) => ({
                source: `${entity}_${mode.toLowerCase()}`,
                entity,
                mode,
                module: moduleKey,
                ...(window.__FORM_CONFIRM_PAYLOAD__ || {}),
                ...payload,
            }));

        // Track event
        const menuPart =
            menuName && menuName !== 'Unknown' ? ` ${menuName}` : '';

        window.amplitudeTracker._safeTrack(
            `${moduleName} - Click Confirm ${mode}${menuPart}`,
            buildProperties({ entity, mode, payload })
        );
    } catch (error) {
        console.error('[Amplitude] Error in trackGlobalFormConfirm:', error);
    }
};



/*===== Create / Update Cancel ========*/
window.trackGlobalFormCancel = function (entity, mode) {
    try {
        if (!window.amplitudeTracker) return;
        if (!entity || !mode) {
            console.warn('[Amplitude] Missing entity or mode for form cancel');
            return;
        }

        const moduleName = typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown';
        const menuName   = typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown';

        window.amplitudeTracker._safeTrack(
            `${moduleName} - Click Cancel ${mode} ${menuName}`,
            {
                source: `${entity}_${mode}`,
                entity: entity,
                mode: mode
            }
        );
    } catch (error) {
        console.error('[Amplitude] Error in trackGlobalFormCancel:', error);
    }
};

/*===== Backward-compatible Create wrappers ========*/
window.trackGlobalCreateClick = function (entity, payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        if (!entity) {
            console.warn('[Amplitude] Missing entity for create confirm');
            return;
        }

        if (typeof window.trackGlobalFormConfirm === 'function') {
            window.trackGlobalFormConfirm(entity, 'Create', payload);
        } else {
            const moduleName = typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown';
            const menuName   = typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown';
            window.amplitudeTracker._safeTrack(
                `${moduleName} - Click Confirm Create ${menuName}`,
                { source: `${entity}_create`, entity, mode: 'Create', ...payload }
            );
        }

        // important when redirect happens immediately
        window.amplitudeTracker.flush();
    } catch (error) {
        console.error('[Amplitude] Error in trackGlobalCreateClick:', error);
    }
};

window.trackGlobalCreateCancel = function (entity) {
    try {
        if (!window.amplitudeTracker) return;
        if (!entity) {
            console.warn('[Amplitude] Missing entity for create cancel');
            return;
        }

        if (typeof window.trackGlobalFormCancel === 'function') {
            window.trackGlobalFormCancel(entity, 'Create');
        } else {
            const moduleName = typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown';
            const menuName   = typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown';
            window.amplitudeTracker._safeTrack(
                `${moduleName} - Click Cancel Create ${menuName}`,
                { source: `${entity}_create`, entity, mode: 'Create' }
            );
        }

        window.amplitudeTracker.flush();
    } catch (error) {
        console.error('[Amplitude] Error in trackGlobalCreateCancel:', error);
    }
};

/* ================= GLOBAL FORM Footer NAVIGATION TRACKING ================= */
window.trackGlobalFormBack = function (entity, mode) {
    if (!window.amplitudeTracker) return;

    const moduleName = getCurrentModule();
    const menuName = getCurrentMenu();

    amplitudeTracker.track(
        `${moduleName} - Click Back ${mode} ${menuName}`,
        {
            source: `${entity}_${mode.toLowerCase()}`,
            entity: entity,
            mode: mode,
            navigation_action: 'back',
            page_title: document.title,
            current_step: getCurrentFormStep() // Optional: add step tracking
        }
    );
};

window.trackGlobalFormNext = function (entity, mode) {
    if (!window.amplitudeTracker) return;

    const moduleName = getCurrentModule();
    const menuName = getCurrentMenu();

    amplitudeTracker.track(
        `${moduleName} - Click Next ${mode} ${menuName}`,
        {
            source: `${entity}_${mode.toLowerCase()}`,
            entity: entity,
            mode: mode,
            navigation_action: 'next',
            page_title: document.title,
            current_step: getCurrentFormStep() // Optional: add step tracking
        }
    );
};

// Optional: Helper function to get current form step
function getCurrentFormStep() {
    // If you have multi-step forms, implement this
    // Example: check URL hash or active tab
    const hash = window.location.hash;
    if (hash) return hash.replace('#', '');
    
    // Or check active tab
    const activeTab = document.querySelector('.nav-link.active, .step-active');
    if (activeTab) return activeTab.textContent.trim();
    
    return 'step_1';
}


window.trackGlobalFormResult = function (
    entity,
    mode,          // 'Create' | 'Update'
    status,        // 'success' | 'fail'
    entityId = null,
    error = null,
    errorCode = null,
    formAcrissCode = null,
    formRateBookSnapshot = null,
    formPayload = null
) {
    try {
        if (!window.amplitudeTracker) return;
        if (!entity || !mode || !status) {
            console.warn('[Amplitude] Missing required parameters for form result');
            return;
        }

        const modeLower = String(mode || '').trim().toLowerCase();
        const normalizedMode =
            modeLower === 'update'
                ? 'Update'
                : modeLower === 'publish'
                  ? 'Publish'
                  : 'Create';

        const moduleName = typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown';
        const menuName   = typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown';

        const entityLower = String(entity || '').toLowerCase();
        const successFormPayload =
            status === 'success' &&
            formPayload &&
            typeof formPayload === 'object' &&
            !Array.isArray(formPayload)
                ? formPayload
                : {};

        // Damage Alert update tracking is handled explicitly in `layouts/admin.blade.php`.
        // Skip the generic "Update ..." form result event to avoid duplicates.
        if (entityLower === 'reservationreport' && normalizedMode === 'Update') {
            return;
        }

        // Cancellation invoice (cancelled reservations) uses the generic `invoice` endpoint.
        // We emit reservation-specific events from `plugin/alert.js`, so suppress the
        // generic invoice Create success/fail event across the reload.
        if (entityLower === 'invoice' && normalizedMode === 'Create') {
            try {
                const key =
                    'amplitude_suppress_invoice_create_for_cancelled_reservation_invoice';
                const suppress = sessionStorage.getItem(key);
                if (suppress) {
                    sessionStorage.removeItem(key);
                    return;
                }
            } catch (e) {
                /* ignore */
            }
        }

        if (
            entityLower === 'exchange_rate' &&
            normalizedMode === 'Update' &&
            status === 'success'
        ) {
            window.amplitudeTracker._safeTrack('Rates - Update Exchange Rate Success', {
                source: 'exchange_rate_update',
                entity: entity,
                entity_id: entityId,
                exchange_rate_id:
                    entityId != null ? String(entityId) : null,
                mode: normalizedMode,
                status: status
            });
        } else if (
            entityLower === 'exchange_rate' &&
            normalizedMode === 'Update' &&
            status === 'fail'
        ) {
            window.amplitudeTracker._safeTrack('Rates - Update Exchange Rate Fail', {
                source: 'exchange_rate_update',
                entity: entity,
                entity_id: entityId,
                exchange_rate_id:
                    entityId != null ? String(entityId) : null,
                mode: normalizedMode,
                status: status,
                error_code: errorCode ?? null,
                error_message: error ?? null
            });
        } else if (
            entityLower === 'acriss' &&
            normalizedMode === 'Update' &&
            status === 'success'
        ) {
            const code =
                formAcrissCode != null &&
                String(formAcrissCode).trim() !== ''
                    ? String(formAcrissCode).trim()
                    : null;
            window.amplitudeTracker._safeTrack('Rates - Update Acriss Success', {
                source: 'acriss_update',
                entity: entity,
                entity_id: entityId,
                acriss_id: entityId != null ? String(entityId) : null,
                acriss_code: code,
                mode: normalizedMode,
                status: status
            });
        } else if (
            entityLower === 'acriss' &&
            normalizedMode === 'Update' &&
            status === 'fail'
        ) {
            const code =
                formAcrissCode != null &&
                String(formAcrissCode).trim() !== ''
                    ? String(formAcrissCode).trim()
                    : null;
            window.amplitudeTracker._safeTrack('Rates - Update Acriss Fail', {
                source: 'acriss_update',
                entity: entity,
                entity_id: entityId,
                acriss_id: entityId != null ? String(entityId) : null,
                acriss_code: code,
                mode: normalizedMode,
                status: status,
                error_code: errorCode ?? null,
                error_message: error ?? null
            });
        } else if (
            entityLower === 'rate' &&
            normalizedMode === 'Create' &&
            status === 'success'
        ) {
            window.amplitudeTracker._safeTrack('Rates - Create Rate Book Success', {
                source: 'rate_book_create',
                entity: 'rate',
                rate_id: entityId != null ? String(entityId) : null,
                entity_id: entityId != null ? String(entityId) : null,
                mode: normalizedMode,
                status: status
            });
        } else if (
            entityLower === 'rate' &&
            normalizedMode === 'Create' &&
            status === 'fail'
        ) {
            window.amplitudeTracker._safeTrack('Rates - Create Rate Book Fail', {
                source: 'rate_book_create',
                entity: 'rate',
                rate_id: entityId != null ? String(entityId) : null,
                entity_id: entityId != null ? String(entityId) : null,
                mode: normalizedMode,
                status: status,
                error_code: errorCode ?? null,
                error_message: error ?? null
            });
        } else if (
            entityLower === 'rate' &&
            normalizedMode === 'Update' &&
            status === 'success'
        ) {
            const snap =
                formRateBookSnapshot &&
                typeof formRateBookSnapshot === 'object' &&
                !Array.isArray(formRateBookSnapshot)
                    ? formRateBookSnapshot
                    : {};
            window.amplitudeTracker._safeTrack('Rates - Update Rate Book Success', {
                source: 'rate_book_update',
                entity: 'rate',
                rate_id: entityId != null ? String(entityId) : null,
                entity_id: entityId != null ? String(entityId) : null,
                mode: normalizedMode,
                status: status,
                ...snap
            });
        } else if (
            entityLower === 'rate' &&
            normalizedMode === 'Update' &&
            status === 'fail'
        ) {
            const snap =
                formRateBookSnapshot &&
                typeof formRateBookSnapshot === 'object' &&
                !Array.isArray(formRateBookSnapshot)
                    ? formRateBookSnapshot
                    : {};
            window.amplitudeTracker._safeTrack('Rates - Update Rate Book Fail', {
                source: 'rate_book_update',
                entity: 'rate',
                rate_id: entityId != null ? String(entityId) : null,
                entity_id: entityId != null ? String(entityId) : null,
                mode: normalizedMode,
                status: status,
                error_code: errorCode ?? null,
                error_message: error ?? null,
                ...snap
            });
        } else if (
            entityLower === 'settings' &&
            normalizedMode === 'Update' &&
            status === 'success'
        ) {
            const tabLabel =
                successFormPayload.settings_tab_label != null &&
                String(successFormPayload.settings_tab_label).trim() !== ''
                    ? String(successFormPayload.settings_tab_label).trim()
                    : 'Unknown';
            window.amplitudeTracker._safeTrack(
                `System - Click Update ${tabLabel} Success`,
                {
                    value:
                        successFormPayload.value != null
                            ? String(successFormPayload.value)
                            : null,
                    column_name:
                        successFormPayload.column_name != null
                            ? String(successFormPayload.column_name)
                            : null
                }
            );
        } else if (
            entityLower === 'settings' &&
            normalizedMode === 'Update' &&
            status === 'fail'
        ) {
            const fp =
                formPayload &&
                typeof formPayload === 'object' &&
                !Array.isArray(formPayload)
                    ? formPayload
                    : {};
            const tabLabel =
                fp.settings_tab_label != null &&
                String(fp.settings_tab_label).trim() !== ''
                    ? String(fp.settings_tab_label).trim()
                    : 'Unknown';
            window.amplitudeTracker._safeTrack(
                `System - Click Update ${tabLabel} Fail`,
                {
                    error_code: errorCode ?? null,
                    error_message: error ?? null
                }
            );
        } else if (entityLower === 'vehicle_testing') {
            const fp =
                formPayload &&
                typeof formPayload === 'object' &&
                !Array.isArray(formPayload)
                    ? formPayload
                    : {};
            const actionLabel =
                mode != null && String(mode).trim() !== ''
                    ? String(mode).trim()
                    : 'Unknown';
            window.amplitudeTracker._safeTrack(
                `System - Click ${actionLabel} Vehicle Testing ${status === 'success' ? 'Success' : 'Fail'}`,
                {
                    action: actionLabel,
                    imei_number:
                        fp.imei_number != null &&
                        String(fp.imei_number).trim() !== ''
                            ? String(fp.imei_number).trim()
                            : null,
                    route: fp.route ?? null,
                    updated_count:
                        fp.updated_count != null ? Number(fp.updated_count) : null,
                    api_endpoint: fp.api_endpoint ?? null,
                    message: status === 'success' ? fp.message ?? null : null,
                    error_code:
                        status === 'fail'
                            ? errorCode ?? fp.error_code ?? null
                            : null,
                    error_message:
                        status === 'fail'
                            ? error ?? fp.error_message ?? null
                            : null
                }
            );
        } else {
            window.amplitudeTracker._safeTrack(
                `${moduleName} - Click ${normalizedMode} ${menuName} ${status === 'success' ? 'Success' : 'Fail'}`,
                {
                    source: `${entity}_${normalizedMode.toLowerCase()}`,
                    entity: entity,
                    entity_id: entityId,
                    mode: normalizedMode,
                    status: status,
                    error_code: status === 'fail' ? (errorCode ?? null) : null,
                    error_message: status === 'fail' ? (error ?? null) : null,
                    ...successFormPayload,
                    ...buildEntityIdProperty(entity, entityId),
                    ...(entityLower === 'rate'
                        ? { rate_id: entityId != null ? String(entityId) : null }
                        : {}),
                    ...(entityLower === 'exchange_rate'
                        ? {
                              exchange_rate_id:
                                  entityId != null ? String(entityId) : null
                          }
                        : {})
                }
            );
        }

        // important when redirect happens immediately
        window.amplitudeTracker.flush();
    } catch (error) {
        console.error('[Amplitude] Error in trackGlobalFormResult:', error);
    }
};

/**
 * System Settings page — user clicked Update for the active tab (before submit).
 * @param {string} tabLabel e.g. "General", "Email"
 */
window.trackSystemClickUpdateSettings = function (tabLabel) {
    try {
        if (!window.amplitudeTracker) return;
        const label =
            tabLabel != null && String(tabLabel).trim() !== ''
                ? String(tabLabel).trim()
                : 'Unknown';
        window.amplitudeTracker._safeTrack(`System - Click Update ${label}`, {
            source: 'settings_update',
            tab: label
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickUpdateSettings:', error);
    }
};

/**
 * System sidebar — user clicked the Access nav-group dropdown toggle.
 */
window.trackSystemClickAccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Click Access', {
            source: 'sidebar_nav',
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickAccess:', error);
    }
};

/**
 * Vehicle Testing page — user clicked an action button (before submit).
 * @param {string} actionLabel e.g. "IMEI Update", "Vehicle Data", "Reset Auth", "End trip"
 * @param {object} payload e.g. { imei_number, route, input_name }
 */
window.trackSystemClickVehicleTesting = function (actionLabel, payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const label =
            actionLabel != null && String(actionLabel).trim() !== ''
                ? String(actionLabel).trim()
                : 'Unknown';
        const p =
            payload && typeof payload === 'object' && !Array.isArray(payload)
                ? payload
                : {};
        window.amplitudeTracker._safeTrack(`System - Click ${label} Vehicle Testing`, {
            source: 'vehicle_testing',
            action: label,
            imei_number:
                p.imei_number != null && String(p.imei_number).trim() !== ''
                    ? String(p.imei_number).trim()
                    : null,
            route: p.route ?? null,
            input_name: p.input_name ?? null
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickVehicleTesting:', error);
    }
};

/* ================= COMMAND CENTER - SYSTEM EVENTS ================= */
window.trackSystemSendAuthorizationRFID = function (payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Authorization RFID', {
            button_clicked: 'send',
            vehicle: payload.vehicle_id ?? null,
            vehicle_id: payload.vehicle_id ?? null,
            type: payload.type ?? null,
            timer_minutes: payload.timer ?? null,
            rfid_secret_code: payload.rfid_secret_code ?? null,
            ...payload,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendAuthorizationRFID:', error);
    }
};

window.trackSystemSendAuthorizationRFIDSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'System - Send Authorization RFID Success',
            {}
        );
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error(
            '[Amplitude] Error in trackSystemSendAuthorizationRFIDSuccess:',
            error
        );
    }
};

window.trackSystemSendAuthorizationRFIDFail = function (
    errorCode = null,
    errorMessage = null
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Authorization RFID Fail', {
            error_code:
                errorCode != null && String(errorCode).trim() !== ''
                    ? String(errorCode)
                    : null,
            error_message:
                errorMessage != null && String(errorMessage).trim() !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendAuthorizationRFIDFail:', error);
    }
};

window.trackSystemClickCancelAuthorizationRFID = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'System - Click Cancel Authorization RFID',
            { button_clicked: 'cancel' }
        );
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error(
            '[Amplitude] Error in trackSystemClickCancelAuthorizationRFID:',
            error
        );
    }
};

window.trackSystemSendCarSharing = function (payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Car Sharing', {
            button_clicked: 'send',
            vehicle: payload.vehicle_id ?? null,
            vehicle_id: payload.vehicle_id ?? null,
            carsharing_status: payload.carshare_status ?? null,
            status: payload.vehicle_status ?? null,
            ...payload,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendCarSharing:', error);
    }
};

window.trackSystemSendCarSharingSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Car Sharing Success', {});
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendCarSharingSuccess:', error);
    }
};

window.trackSystemSendCarSharingFail = function (
    errorCode = null,
    errorMessage = null
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Car Sharing Fail', {
            error_code:
                errorCode != null && String(errorCode).trim() !== ''
                    ? String(errorCode)
                    : null,
            error_message:
                errorMessage != null && String(errorMessage).trim() !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendCarSharingFail:', error);
    }
};

window.trackSystemClickCancelCarSharing = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Click Cancel Car Sharing', {
            button_clicked: 'cancel',
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickCancelCarSharing:', error);
    }
};

window.trackSystemSendAuthorizingBluetooth = function (payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Authorizing Bluetooth', {
            button_clicked: 'send',
            reservation: payload.reservation_id ?? null,
            reservation_id: payload.reservation_id ?? null,
            ...payload,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendAuthorizingBluetooth:', error);
    }
};

window.trackSystemSendAuthorizingBluetoothSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'System - Send Authorizing Bluetooth Success',
            {}
        );
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error(
            '[Amplitude] Error in trackSystemSendAuthorizingBluetoothSuccess:',
            error
        );
    }
};

window.trackSystemSendAuthorizingBluetoothFail = function (
    errorCode = null,
    errorMessage = null
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'System - Send Authorizing Bluetooth Fail',
            {
                error_code:
                    errorCode != null && String(errorCode).trim() !== ''
                        ? String(errorCode)
                        : null,
                error_message:
                    errorMessage != null && String(errorMessage).trim() !== ''
                        ? String(errorMessage)
                        : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendAuthorizingBluetoothFail:', error);
    }
};

window.trackSystemClickCancelAuthorizingBluetooth = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'System - Click Cancel Authorizing Bluetooth',
            { button_clicked: 'cancel' }
        );
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error(
            '[Amplitude] Error in trackSystemClickCancelAuthorizingBluetooth:',
            error
        );
    }
};

window.trackSystemSendBluetoothClear = function (payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Bluetooth Clear', {
            button_clicked: 'send',
            reservation: payload.reservation_id ?? null,
            reservation_id: payload.reservation_id ?? null,
            ...payload,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendBluetoothClear:', error);
    }
};

window.trackSystemSendBluetoothClearSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Bluetooth Clear Success', {});
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendBluetoothClearSuccess:', error);
    }
};

window.trackSystemSendBluetoothClearFail = function (
    errorCode = null,
    errorMessage = null
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Bluetooth Clear Fail', {
            error_code:
                errorCode != null && String(errorCode).trim() !== ''
                    ? String(errorCode)
                    : null,
            error_message:
                errorMessage != null && String(errorMessage).trim() !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendBluetoothClearFail:', error);
    }
};

window.trackSystemClickCancelBluetoothClear = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'System - Click Cancel Bluetooth Clear',
            { button_clicked: 'cancel' }
        );
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickCancelBluetoothClear:', error);
    }
};

window.trackSystemSendDoorLock = function (payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Door Lock', {
            button_clicked: 'send',
            vehicle: payload.vehicle_id ?? null,
            vehicle_id: payload.vehicle_id ?? null,
            ...payload,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendDoorLock:', error);
    }
};

window.trackSystemSendDoorLockSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Door Lock Success', {});
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendDoorLockSuccess:', error);
    }
};

window.trackSystemSendDoorLockFail = function (
    errorCode = null,
    errorMessage = null
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Door Lock Fail', {
            error_code:
                errorCode != null && String(errorCode).trim() !== ''
                    ? String(errorCode)
                    : null,
            error_message:
                errorMessage != null && String(errorMessage).trim() !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendDoorLockFail:', error);
    }
};

window.trackSystemClickCancelDoorLock = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Click Cancel Door Lock', {
            button_clicked: 'cancel',
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickCancelDoorLock:', error);
    }
};

window.trackSystemSendDoorUnlock = function (payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Door Unlock', {
            button_clicked: 'send',
            vehicle: payload.vehicle_id ?? null,
            vehicle_id: payload.vehicle_id ?? null,
            ...payload,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendDoorUnlock:', error);
    }
};

window.trackSystemSendDoorUnlockSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Door Unlock Success', {});
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendDoorUnlockSuccess:', error);
    }
};

window.trackSystemSendDoorUnlockFail = function (
    errorCode = null,
    errorMessage = null
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Door Unlock Fail', {
            error_code:
                errorCode != null && String(errorCode).trim() !== ''
                    ? String(errorCode)
                    : null,
            error_message:
                errorMessage != null && String(errorMessage).trim() !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendDoorUnlockFail:', error);
    }
};

window.trackSystemClickCancelDoorUnlock = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Click Cancel Door Unlock', {
            button_clicked: 'cancel',
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickCancelDoorUnlock:', error);
    }
};

window.trackSystemSendEngineKill = function (payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Engine Kill', {
            button_clicked: 'send',
            vehicle: payload.vehicle_id ?? null,
            vehicle_id: payload.vehicle_id ?? null,
            engine_status: payload.engine_status ?? null,
            ...payload,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendEngineKill:', error);
    }
};

window.trackSystemSendEngineKillSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Engine Kill Success', {});
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendEngineKillSuccess:', error);
    }
};

window.trackSystemSendEngineKillFail = function (
    errorCode = null,
    errorMessage = null
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Engine Kill Fail', {
            error_code:
                errorCode != null && String(errorCode).trim() !== ''
                    ? String(errorCode)
                    : null,
            error_message:
                errorMessage != null && String(errorMessage).trim() !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendEngineKillFail:', error);
    }
};

window.trackSystemClickCancelEngineKill = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Click Cancel Engine Kill', {
            button_clicked: 'cancel',
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickCancelEngineKill:', error);
    }
};

window.trackSystemSendChangeOdometer = function (payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Change Odometer', {
            button_clicked: 'send',
            vehicle: payload.vehicle_id ?? null,
            vehicle_id: payload.vehicle_id ?? null,
            odometer: payload.odometer ?? null,
            ...payload,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendChangeOdometer:', error);
    }
};

window.trackSystemSendChangeOdometerSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Change Odometer Success', {});
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendChangeOdometerSuccess:', error);
    }
};

window.trackSystemSendChangeOdometerFail = function (
    errorCode = null,
    errorMessage = null
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Send Change Odometer Fail', {
            error_code:
                errorCode != null && String(errorCode).trim() !== ''
                    ? String(errorCode)
                    : null,
            error_message:
                errorMessage != null && String(errorMessage).trim() !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemSendChangeOdometerFail:', error);
    }
};

window.trackSystemClickCancelChangeOdometer = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('System - Click Cancel Change Odometer', {
            button_clicked: 'cancel',
        });
        window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackSystemClickCancelChangeOdometer:', error);
    }
};

/* ================= WIZARD-STYLE GLOBAL EVENTS ================= */
// menuOverride: undefined = append getCurrentMenu(); null/false = no suffix; string = that suffix.
function _wizardMenuPartFromOverride(menuOverride, menuName) {
    if (menuOverride === undefined) {
        return menuName && menuName !== 'Unknown' ? ` ${menuName}` : '';
    }
    if (menuOverride === null || menuOverride === false) {
        return '';
    }
    const s = String(menuOverride).trim();
    return s ? ` ${s}` : '';
}

function _wizardStepLabelProp(menuOverride, menuName) {
    if (menuOverride === undefined) {
        return menuName && menuName !== 'Unknown' ? menuName : null;
    }
    if (menuOverride === null || menuOverride === false) {
        return null;
    }
    const s = String(menuOverride).trim();
    return s || null;
}

// Generic wrapper for multi-step form clicks/results where we want consistent
// module/menu based naming, but also need custom payload (e.g. email, ERP).
window.trackGlobalWizardStepClick = function (entity, actionLabel, payload = {}, flush = false, menuOverride = undefined) {
    try {
        if (!window.amplitudeTracker) return;
        if (!entity || !actionLabel) return;

        const moduleName =
            typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown';
        let menuName =
            typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown';
        const entityLower = String(entity).toLowerCase();
        if (menuName === 'Unknown' && (entityLower === 'customer' || entityLower === 'customers')) {
            menuName = 'Customers';
        }

        const safeAction = String(actionLabel);
        const normalizedActionKey = safeAction
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '_');

        const menuPart = _wizardMenuPartFromOverride(menuOverride, menuName);
        const stepLabel = _wizardStepLabelProp(menuOverride, menuName);

        window.amplitudeTracker._safeTrack(
            `${moduleName} - Click ${safeAction}${menuPart}`,
            {
                source: `${entity}_${normalizedActionKey}`,
                entity: entity,
                action_label: safeAction,
                wizard_step_label: stepLabel,
                ...(payload || {})
            }
        );

        if (flush) window.amplitudeTracker.flush?.();
    } catch (error) {
        console.error('[Amplitude] Error in trackGlobalWizardStepClick:', error);
    }
};

window.trackGlobalWizardStepResult = function (
    entity,
    actionLabel,
    status, // 'success' | 'fail'
    entityId = null,
    error = null,
    errorCode = null,
    payload = {},
    menuOverride = undefined
) {
    try {
        if (!window.amplitudeTracker) return;
        if (!entity || !actionLabel || !status) return;

        const moduleName =
            typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown';
        let menuName =
            typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown';
        const entityLower = String(entity).toLowerCase();
        if (menuName === 'Unknown' && (entityLower === 'customer' || entityLower === 'customers')) {
            menuName = 'Customers';
        }

        const normalizedStatus = String(status).toLowerCase() === 'success' ? 'success' : 'fail';
        const resultLabel = normalizedStatus === 'success' ? 'Success' : 'Fail';

        const safeAction = String(actionLabel);

        const menuPart = _wizardMenuPartFromOverride(menuOverride, menuName);
        const stepLabel = _wizardStepLabelProp(menuOverride, menuName);

        const safeActionTrim = safeAction.trim();
        const actionLower = safeActionTrim.toLowerCase();
        const isCreateAction = actionLower.startsWith('create');
        const includeClickPrefix = !isCreateAction && /personal information/i.test(safeActionTrim);

        const eventName = includeClickPrefix
            ? `${moduleName} - Click ${safeActionTrim}${menuPart} ${resultLabel}`
            : `${moduleName} - ${safeActionTrim}${menuPart} ${resultLabel}`;

        window.amplitudeTracker._safeTrack(
            eventName,
            {
                source: `${entity}_${safeAction.toLowerCase().trim().replace(/\s+/g, '_')}`,
                entity: entity,
                entity_id: entityId ? entityId : null,
                action_label: safeAction,
                wizard_step_label: stepLabel,
                status: normalizedStatus,
                error_code: normalizedStatus === 'fail' ? (errorCode ?? null) : null,
                error_message: normalizedStatus === 'fail' ? (error ?? null) : null,
                ...(payload || {})
            }
        );

        window.amplitudeTracker.flush();
    } catch (error) {
        console.error('[Amplitude] Error in trackGlobalWizardStepResult:', error);
    }
};

/** Fleet vehicle create/edit wizard: reads #step-input (1–3). Uses trackGlobalWizardStepClick + Vehicles module. */
window.trackFleetVehicleWizardStepNumber = function () {
    const el = document.getElementById('step-input');
    const n = el ? parseInt(String(el.value), 10) : 1;
    return Number.isFinite(n) && n >= 1 && n <= 3 ? n : 1;
};

window.trackFleetVehicleWizardBack = function () {
    const step = window.trackFleetVehicleWizardStepNumber();
    if (step === 1) {
        window.trackGlobalWizardStepClick('vehicle', 'Back', { fleet_wizard_step: step }, false, 'Vehicle Information');
    } else if (step === 2) {
        window.trackGlobalWizardStepClick('vehicle', 'Back', { fleet_wizard_step: step }, false, 'Gallery');
    } else if (step === 3) {
        window.trackGlobalWizardStepClick('vehicle', 'Back', { fleet_wizard_step: step }, false, 'Vehicle Detail');
    }
};

window.trackFleetVehicleWizardNext = function () {
    const step = window.trackFleetVehicleWizardStepNumber();
    if (step === 1) {
        window.trackGlobalWizardStepClick('vehicle', 'Next', { fleet_wizard_step: step }, false, 'Vehicle Information');
    } else if (step === 2) {
        window.trackGlobalWizardStepClick('vehicle', 'Next', { fleet_wizard_step: step }, false, 'Gallery');
    } else if (step === 3) {
        window.trackGlobalWizardStepClick('vehicle', 'Next', { fleet_wizard_step: step }, false, 'Vehicle Detail');
    }
};

/** Primary submit / Save & Continue / Save — call before form posts (flush so redirect does not drop event). */
window.trackFleetVehicleWizardSaveClick = function () {
    const step = window.trackFleetVehicleWizardStepNumber();
    if (step === 1) {
        window.trackGlobalWizardStepClick(
            'vehicle',
            'Save And Continue',
            { fleet_wizard_step: step },
            false,
            'Vehicle Information'
        );
    } else if (step === 2) {
        window.trackGlobalWizardStepClick(
            'vehicle',
            'Save And Continue',
            { fleet_wizard_step: step },
            false,
            'Gallery'
        );
    } else if (step === 3) {
        window.trackGlobalWizardStepClick(
            'vehicle',
            'Save Vehicle Detail',
            { fleet_wizard_step: step },
            false,
            null
        );
    }
};

/* ================= CUSTOMER UPDATE (EDIT) EVENTS ================= */
// Admin Management reuses the same user edit form; Amplitude module prefix is "System" on /adminmanagement/*.
function amplitudeCustomerUpdateEventPrefix() {
    const p =
        typeof location !== 'undefined'
            ? String(location.pathname || '').toLowerCase()
            : '';
    return p.includes('adminmanagement') ? 'System' : 'Customers';
}

// Fixed event names per spec; uses same tracker + property patterns as other modules.
window.trackCustomerUpdateCustomerClick = function (customerId, source) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        const resolvedSource = source || 'customer_edit';
        // Edit page inline script can run more than once (e.g. duplicate DOM init); only one page-view event.
        if (resolvedSource === 'admin_edit' || resolvedSource === 'customer_edit') {
            if (!window.__ampTcuPageViewOnce) {
                window.__ampTcuPageViewOnce = new Set();
            }
            const dedupeKey = String(customerId) + '\0' + resolvedSource;
            if (window.__ampTcuPageViewOnce.has(dedupeKey)) {
                return;
            }
            window.__ampTcuPageViewOnce.add(dedupeKey);
        }
        const mod = amplitudeCustomerUpdateEventPrefix();
        window.amplitudeTracker._safeTrack(`${mod} - Click Update Customer`, {
            customer_id: String(customerId),
            source: resolvedSource,
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateCustomerClick failed:', e);
    }
};

window.trackCustomerUpdateIdentificationClick = function (customerId, source) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        const mod = amplitudeCustomerUpdateEventPrefix();
        window.amplitudeTracker._safeTrack(`${mod} - Click Update Identification`, {
            customer_id: String(customerId),
            source: source || 'customer_identification_edit',
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateIdentificationClick failed:', e);
    }
};

window.trackCustomerUpdateNextClick = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const mod = amplitudeCustomerUpdateEventPrefix();
        window.amplitudeTracker._safeTrack(
            `${mod} - Click Next Update Customer`,
            props
        );
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateNextClick failed:', e);
    }
};

window.trackCustomerUpdateConfirmClick = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const mod = amplitudeCustomerUpdateEventPrefix();
        window.amplitudeTracker._safeTrack(
            `${mod} - Click Confirm Update Customer`,
            props
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateConfirmClick failed:', e);
    }
};

window.trackCustomerUpdateSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        const mod = amplitudeCustomerUpdateEventPrefix();
        window.amplitudeTracker._safeTrack(`${mod} - Update Customer Success`, {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateSuccess failed:', e);
    }
};

window.trackCustomerUpdateFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        const mod = amplitudeCustomerUpdateEventPrefix();
        window.amplitudeTracker._safeTrack(`${mod} - Update Customer Fail`, {
            customer_id: String(customerId),
            error_code:
                errorCode != null && String(errorCode) !== '' ? errorCode : null,
            error_message:
                errorMessage != null && String(errorMessage) !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateFail failed:', e);
    }
};

window.trackCustomerUpdateCancelClick = function (customerId, source) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        const mod = amplitudeCustomerUpdateEventPrefix();
        window.amplitudeTracker._safeTrack(
            `${mod} - Click Cancel Update Customer`,
            {
                customer_id: String(customerId),
                source: source || 'customer_update_cancel',
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateCancelClick failed:', e);
    }
};

window.trackSystemClickBackUpdateCustomers = function (customerId = null) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'System - Click Back Update Customers',
            {
                source: 'customer_update',
                customer_id:
                    customerId != null && String(customerId).trim() !== ''
                        ? String(customerId).trim()
                        : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackSystemClickBackUpdateCustomers failed:', e);
    }
};

window.trackSystemClickBackDeleteCustomers = function (
    customerId = null,
    actionType = 'soft_delete'
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'System - Click Back Delete Customers',
            {
                source: 'admin_list',
                action_type: actionType || 'soft_delete',
                customer_id:
                    customerId != null && String(customerId).trim() !== ''
                        ? String(customerId).trim()
                        : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackSystemClickBackDeleteCustomers failed:', e);
    }
};

/* ================= CUSTOMER OVERVIEW (DETAIL SHOW) EVENTS ================= */
window.trackCustomerBackOverviewClick = function (customerId, source, companyName = null, companyId = null) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;

        const props = {
            source: source || 'overview',
            customer_id: String(customerId),
        };

        if (companyName != null || companyId != null) {
            props.company_name = companyName || null;
            props.company_id = companyId || null;
        }

        window.amplitudeTracker._safeTrack('Customers - Click Back Overview', props);
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerBackOverviewClick failed:', e);
    }
};

window.trackCustomerEditOverviewClick = function (customerId, source, companyName = null, companyId = null) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;

        const props = {
            source: source || 'overview',
            customer_id: String(customerId),
        };

        if (companyName != null || companyId != null) {
            props.company_name = companyName || null;
            props.company_id = companyId || null;
        }

        window.amplitudeTracker._safeTrack('Customers - Click Edit Overview', props);
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerEditOverviewClick failed:', e);
    }
};

/* ================= EMERGENCY CONTACT EVENTS ================= */
function _getEmergencyContactFormFields() {
    const form = document.getElementById('create-form');
    if (!form) return {};

    const props = {};
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
        // remove technical fields
        if (key === '_token' || key === '_method') continue;

        if (Object.prototype.hasOwnProperty.call(props, key)) {
            if (Array.isArray(props[key])) {
                props[key].push(value);
            } else {
                props[key] = [props[key], value];
            }
        } else {
            props[key] = value;
        }
    }

    return props;
}

window.trackCustomerClickAddEmergencyContacts = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Click Add Emergency Contacts',
            {}
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerClickAddEmergencyContacts failed:', e);
    }
};

window.trackCustomerClickEditEmergencyContacts = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Click Edit Emergency Contacts',
            {}
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerClickEditEmergencyContacts failed:', e);
    }
};

window.trackCustomerConfirmCreateEmergencyContacts = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Create Emergency Contacts',
            props
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmCreateEmergencyContacts failed:', e);
    }
};

window.trackCustomerConfirmCreateEmergencyContactsSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Create Emergency Contacts Success',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmCreateEmergencyContactsSuccess failed:', e);
    }
};

window.trackCustomerConfirmCreateEmergencyContactsFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Create Emergency Contacts Fail',
            {
                error_code: errorCode != null ? errorCode : null,
                error_message: errorMessage != null ? errorMessage : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmCreateEmergencyContactsFail failed:', e);
    }
};

window.trackCustomerUpdateEmergencyContacts = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Update Emergency Contacts',
            props
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateEmergencyContacts failed:', e);
    }
};

window.trackCustomerUpdateEmergencyContactsSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Update Emergency Contacts Success',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateEmergencyContactsSuccess failed:', e);
    }
};

window.trackCustomerUpdateEmergencyContactsFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Update Emergency Contacts Fail',
            {
                error_code: errorCode != null ? errorCode : null,
                error_message: errorMessage != null ? errorMessage : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateEmergencyContactsFail failed:', e);
    }
};

window.trackCustomerClickRemoveEmergencyContacts = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Click Remove Emergency Contacts',
            { customer_id: String(customerId) }
        );
    } catch (e) {
        console.error('[Amplitude] trackCustomerClickRemoveEmergencyContacts failed:', e);
    }
};

window.trackCustomerConfirmRemoveEmergencyContacts = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Remove Emergency Contacts',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmRemoveEmergencyContacts failed:', e);
    }
};

window.trackCustomerConfirmRemoveEmergencyContactsSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Remove Emergency Contacts Success',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmRemoveEmergencyContactsSuccess failed:', e);
    }
};

window.trackCustomerConfirmRemoveEmergencyContactsFail = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Remove Emergency Contacts Fail',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmRemoveEmergencyContactsFail failed:', e);
    }
};

window.trackCustomerCancelRemoveEmergencyContacts = function (errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Click Cancel Remove Emergency Contacts',
            {
                error_code: errorCode != null ? errorCode : null,
                error_message: errorMessage != null ? errorMessage : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelRemoveEmergencyContacts failed:', e);
    }
};

// Click tracking for Add/Edit links on Emergency Contacts index
document.addEventListener('click', function (e) {
    const link = e.target.closest(
        '[data-amplitude-emergency-link-action]'
    );
    if (!link || !window.amplitudeTracker) return;

    const action = link.dataset.amplitudeEmergencyLinkAction;
    if (!action) return;

    if (action === 'Add Emergency Contacts') {
        window.trackCustomerClickAddEmergencyContacts?.();
    } else if (action === 'Edit Emergency Contacts') {
        window.trackCustomerClickEditEmergencyContacts?.();
    }
});

// Confirm Create / Update from Emergency Contacts form
document.addEventListener('click', function (e) {
    const btn = e.target.closest(
        '#create-form button[type="submit"][data-amplitude-emergency-form-mode]'
    );
    if (!btn || !window.amplitudeTracker) return;

    const mode = btn.dataset.amplitudeEmergencyFormMode;
    if (!mode) return;

    const props = _getEmergencyContactFormFields();

    if (mode === 'create') {
        window.trackCustomerConfirmCreateEmergencyContacts?.(props);
    } else if (mode === 'update') {
        window.trackCustomerUpdateEmergencyContacts?.(props);
    }
});

/* ================= EXTRA DRIVER EVENTS ================= */
function _getExtraDriverFormFields() {
    const form = document.getElementById('create-form');
    if (!form) return {};

    const props = {};
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
        if (key === '_token' || key === '_method') continue;

        if (Object.prototype.hasOwnProperty.call(props, key)) {
            if (Array.isArray(props[key])) {
                props[key].push(value);
            } else {
                props[key] = [props[key], value];
            }
        } else {
            props[key] = value;
        }
    }

    return props;
}

window.trackCustomerClickAddExtraDriver = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('Customers - Click Add Extra Driver', {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerClickAddExtraDriver failed:', e);
    }
};

window.trackCustomerClickEditExtraDriver = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('Customers - Click Edit Extra Driver', {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerClickEditExtraDriver failed:', e);
    }
};

window.trackCustomerConfirmCreateExtraDriver = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Create Extra Driver',
            props
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmCreateExtraDriver failed:', e);
    }
};

window.trackCustomerConfirmCreateExtraDriverSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Create Extra Driver Success',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmCreateExtraDriverSuccess failed:', e);
    }
};

window.trackCustomerConfirmCreateExtraDriverFail = function (
    customerId,
    errorCode,
    errorMessage
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Create Extra Driver Fail',
            {
                error_code: errorCode != null ? errorCode : null,
                error_message: errorMessage != null ? errorMessage : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmCreateExtraDriverFail failed:', e);
    }
};

window.trackCustomerUpdateExtraDriver = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Update Extra Driver',
            props
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateExtraDriver failed:', e);
    }
};

window.trackCustomerUpdateExtraDriverSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Update Extra Driver Success',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateExtraDriverSuccess failed:', e);
    }
};

window.trackCustomerUpdateExtraDriverFail = function (
    customerId,
    errorCode,
    errorMessage
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Update Extra Driver Fail',
            {
                error_code: errorCode != null ? errorCode : null,
                error_message: errorMessage != null ? errorMessage : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateExtraDriverFail failed:', e);
    }
};

window.trackCustomerClickRemoveExtraDriver = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Click Remove Extra Driver',
            { customer_id: String(customerId) }
        );
    } catch (e) {
        console.error('[Amplitude] trackCustomerClickRemoveExtraDriver failed:', e);
    }
};

window.trackCustomerConfirmRemoveExtraDriver = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Remove Extra Driver',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmRemoveExtraDriver failed:', e);
    }
};

window.trackCustomerConfirmRemoveExtraDriverSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Remove Extra Driver Success',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmRemoveExtraDriverSuccess failed:', e);
    }
};

window.trackCustomerConfirmRemoveExtraDriverFail = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack(
            'Customers - Confirm Remove Extra Driver Fail',
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmRemoveExtraDriverFail failed:', e);
    }
};

window.trackCustomerCancelRemoveExtraDriver = function (errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Customers - Click Cancel Remove Extra Driver',
            {
                error_code: errorCode != null ? errorCode : null,
                error_message: errorMessage != null ? errorMessage : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelRemoveExtraDriver failed:', e);
    }
};

// Click tracking for Add/Edit links on Extra Driver index
document.addEventListener('click', function (e) {
    const link = e.target.closest('[data-amplitude-extra-driver-link-action]');
    if (!link || !window.amplitudeTracker) return;

    const action = link.dataset.amplitudeExtraDriverLinkAction;
    if (!action) return;

    if (action === 'Add Extra Driver') {
        window.trackCustomerClickAddExtraDriver?.();
    } else if (action === 'Edit Extra Driver') {
        window.trackCustomerClickEditExtraDriver?.();
    }
});

// Confirm Create / Update from Extra Driver form
document.addEventListener('click', function (e) {
    const btn = e.target.closest(
        '#create-form button[type="submit"][data-amplitude-extra-driver-form-mode]'
    );
    if (!btn || !window.amplitudeTracker) return;

    const mode = btn.dataset.amplitudeExtraDriverFormMode;
    if (!mode) return;

    const props = _getExtraDriverFormFields();

    if (mode === 'create') {
        window.trackCustomerConfirmCreateExtraDriver?.(props);
    } else if (mode === 'update') {
        window.trackCustomerUpdateExtraDriver?.(props);
    }
});

/* ================= POINTS (Customer Detail) EVENTS ================= */

function _getPointFormFields() {
    const form = document.getElementById("point-create");
    if (!form) return {};

    const props = {};
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
        // remove technical fields
        if (key === "_token" || key === "_method") continue;

        if (Object.prototype.hasOwnProperty.call(props, key)) {
            if (Array.isArray(props[key])) {
                props[key].push(value);
            } else {
                props[key] = [props[key], value];
            }
        } else {
            props[key] = value;
        }
    }

    return props;
}

window.trackCustomerClickAllPoint = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click All Point", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickAllPoint failed:", e);
    }
};

window.trackCustomerClickViewDetailPoint = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click View Detail Point", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickViewDetailPoint failed:", e);
    }
};

window.trackCustomerClickBackViewDetailPoint = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            "Customers - Click Back View Detail Point",
            {}
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickBackViewDetailPoint failed:", e);
    }
};

window.trackCustomerClickCancelCreatePoint = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Cancel Create Point", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickCancelCreatePoint failed:", e);
    }
};

window.trackCustomerClickCancelUpdatePoint = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Cancel Update Point", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickCancelUpdatePoint failed:", e);
    }
};

window.trackCustomerClickUpdatePoint = function (source) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Update Point", {
            source: source != null ? String(source) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickUpdatePoint failed:", e);
    }
};

window.trackCustomerConfirmCreatePoint = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Confirm Create Point", {
            source: props.source != null ? String(props.source) : null,
            point_category: props.point_category != null ? String(props.point_category) : null,
            point_amount: props.point_amount != null ? String(props.point_amount) : null,
            expiry_date: props.expiry_date != null ? String(props.expiry_date) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerConfirmCreatePoint failed:", e);
    }
};

window.trackCustomerConfirmCreatePointSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            "Customers - Click Confirm Create Point Success",
            {}
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerConfirmCreatePointSuccess failed:", e);
    }
};

window.trackCustomerConfirmCreatePointFail = function (errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Confirm Create Point Fail", {
            error_code: errorCode != null ? String(errorCode) : null,
            error_message: errorMessage != null ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerConfirmCreatePointFail failed:", e);
    }
};

window.trackCustomerConfirmUpdatePoint = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            "Customers - Click Confirm Update Point",
            {
                point_id: props.point_id != null ? String(props.point_id) : null,
                point_category:
                    props.point_category != null ? String(props.point_category) : null,
                point_amount:
                    props.point_amount != null ? String(props.point_amount) : null,
                expiry_date:
                    props.expiry_date != null ? String(props.expiry_date) : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerConfirmUpdatePoint failed:", e);
    }
};

window.trackCustomerUpdatePointSuccess = function (pointId) {
    try {
        if (!window.amplitudeTracker || pointId == null || pointId === "") return;
        window.amplitudeTracker._safeTrack("Customers - Update Point Success", {
            point_id: String(pointId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerUpdatePointSuccess failed:", e);
    }
};

window.trackCustomerUpdatePointFail = function (errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Update Point Fail", {
            error_code: errorCode != null ? String(errorCode) : null,
            error_message: errorMessage != null ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerUpdatePointFail failed:", e);
    }
};

window.trackCustomerClickDeletePoint = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Delete Point", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickDeletePoint failed:", e);
    }
};

window.trackCustomerConfirmDeletePoint = function (pointId) {
    try {
        if (!window.amplitudeTracker || pointId == null || pointId === "") return;
        window.amplitudeTracker._safeTrack("Customers - Click Confirm Delete Point", {
            point_id: String(pointId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerConfirmDeletePoint failed:", e);
    }
};

window.trackCustomerDeletePointSuccess = function (pointId) {
    try {
        if (!window.amplitudeTracker || pointId == null || pointId === "") return;
        window.amplitudeTracker._safeTrack("Customers - Delete Point Success", {
            point_id: String(pointId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerDeletePointSuccess failed:", e);
    }
};

window.trackCustomerDeletePointFail = function (errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Delete Point Fail", {
            error_code: errorCode != null ? String(errorCode) : null,
            error_message: errorMessage != null ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerDeletePointFail failed:", e);
    }
};

window.trackCustomerCancelDeletePoint = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Cancel Delete Point", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerCancelDeletePoint failed:", e);
    }
};

// Click tracking for Points tab links
document.addEventListener("click", function (e) {
    const link = e.target.closest("[data-amplitude-point-link-action]");
    if (!link || !window.amplitudeTracker) return;

    const action = link.dataset.amplitudePointLinkAction;
    if (!action) return;

    if (action === "All Points") {
        window.trackCustomerClickAllPoint?.();
    }

    if (action === "View Detail Point") {
        window.trackCustomerClickViewDetailPoint?.();
    }
});

// Back view detail tracking
document.addEventListener("click", function (e) {
    const link = e.target.closest("[data-amplitude-point-back-view-detail]");
    if (!link || !window.amplitudeTracker) return;
    window.trackCustomerClickBackViewDetailPoint?.();
});

// Cancel create/update tracking
document.addEventListener("click", function (e) {
    const link = e.target.closest("[data-amplitude-point-cancel-mode]");
    if (!link || !window.amplitudeTracker) return;

    const mode = link.dataset.amplitudePointCancelMode;
    if (!mode) return;

    if (mode === "create") {
        window.trackCustomerClickCancelCreatePoint?.();
    } else if (mode === "update") {
        window.trackCustomerClickCancelUpdatePoint?.();
    }
});

// Edit click tracking
document.addEventListener("click", function (e) {
    const link = e.target.closest("[data-amplitude-point-edit-source]");
    if (!link || !window.amplitudeTracker) return;

    const source = link.dataset.amplitudePointEditSource;
    window.trackCustomerClickUpdatePoint?.(source);
});

// Confirm Create / Update from Points form
document.addEventListener("click", function (e) {
    const btn = e.target.closest(
        '#point-create button[type="submit"][data-amplitude-point-form-mode]'
    );
    if (!btn || !window.amplitudeTracker) return;

    const mode = btn.dataset.amplitudePointFormMode;
    if (!mode) return;

    const formFields = _getPointFormFields();
    const point_category = formFields.point_category != null ? formFields.point_category : null;
    const point_amount = formFields.point_amount != null ? formFields.point_amount : null;
    const expiry_at = formFields.expiry_at != null ? formFields.expiry_at : null;
    const expiry_date = expiry_at;

    if (mode === "create") {
        window.trackCustomerConfirmCreatePoint?.({
            source: btn.dataset.amplitudePointSource,
            point_category: point_category,
            point_amount: point_amount,
            expiry_date: expiry_date,
        });
    } else if (mode === "update") {
        window.trackCustomerConfirmUpdatePoint?.({
            point_id: btn.dataset.amplitudePointId,
            point_category: point_category,
            point_amount: point_amount,
            expiry_date: expiry_date,
        });
    }
});

/* ================= COUPONS (Customer Detail) EVENTS ================= */
window.trackCustomerClickViewDetailCoupon = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click View Detail Coupon", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickViewDetailCoupon failed:", e);
    }
};

window.trackCustomerClickBackViewDetailCoupon = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Back View Detail Coupon", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickBackViewDetailCoupon failed:", e);
    }
};

document.addEventListener("click", function (e) {
    const link = e.target.closest("[data-amplitude-coupon-link-action]");
    if (!link || !window.amplitudeTracker) return;
    const action = link.dataset.amplitudeCouponLinkAction;
    if (action === "View Detail Coupon") {
        window.trackCustomerClickViewDetailCoupon?.();
    }
});

document.addEventListener("click", function (e) {
    const link = e.target.closest("[data-amplitude-coupon-back-view-detail]");
    if (!link || !window.amplitudeTracker) return;
    window.trackCustomerClickBackViewDetailCoupon?.();
});

/* ================= REVIEWS (Customer Detail) EVENTS ================= */
window.trackCustomerClickViewDetailReview = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click View Detail Review", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickViewDetailReview failed:", e);
    }
};

window.trackCustomerClickBackViewDetailReview = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Back View Detail Review", {});
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickBackViewDetailReview failed:", e);
    }
};

document.addEventListener("click", function (e) {
    const link = e.target.closest("[data-amplitude-review-link-action]");
    if (!link || !window.amplitudeTracker) return;
    const action = link.dataset.amplitudeReviewLinkAction;
    if (action === "View Detail Review") {
        window.trackCustomerClickViewDetailReview?.();
    }
});

document.addEventListener("click", function (e) {
    const link = e.target.closest("[data-amplitude-review-back-view-detail]");
    if (!link || !window.amplitudeTracker) return;
    window.trackCustomerClickBackViewDetailReview?.();
});

/* ================= PRICE MANAGEMENT — REFUND UPLOAD ================= */
window.trackPriceManagementRefundDownloadTemplateClick = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Price Management - Click Download Template',
            {
                source: 'refund_upload',
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error(
            '[Amplitude] trackPriceManagementRefundDownloadTemplateClick failed:',
            e
        );
    }
};

window.trackPriceManagementRefundUploadClick = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const p =
            props && typeof props === 'object' && !Array.isArray(props)
                ? props
                : {};
        window.amplitudeTracker._safeTrack('Price Management - Click Upload', {
            source: 'refund_upload',
            ...p,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackPriceManagementRefundUploadClick failed:', e);
    }
};

window.trackPriceManagementRefundUploadSuccess = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const p =
            props && typeof props === 'object' && !Array.isArray(props)
                ? props
                : {};
        window.amplitudeTracker._safeTrack('Price Management - Upload Success', {
            source: 'refund_upload',
            ...p,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error(
            '[Amplitude] trackPriceManagementRefundUploadSuccess failed:',
            e
        );
    }
};

window.trackPriceManagementRefundUploadFail = function (
    errorCode,
    errorMessage,
    props = {}
) {
    try {
        if (!window.amplitudeTracker) return;
        const p =
            props && typeof props === 'object' && !Array.isArray(props)
                ? props
                : {};
        window.amplitudeTracker._safeTrack('Price Management - Upload Fail', {
            source: 'refund_upload',
            error_code: errorCode != null ? String(errorCode) : null,
            error_message:
                errorMessage != null ? String(errorMessage) : null,
            ...p,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackPriceManagementRefundUploadFail failed:', e);
    }
};

window.trackPriceManagementRefundUploadCancelClick = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            'Price Management - Click Cancel Upload',
            {
                source: 'refund_upload',
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error(
            '[Amplitude] trackPriceManagementRefundUploadCancelClick failed:',
            e
        );
    }
};

/* ================= PAYMENT (Customer Detail) EVENTS ================= */
window.trackCustomerClickPayNow = function (invoiceNumber) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Pay Now", {
            invoice_number:
                invoiceNumber != null && String(invoiceNumber) !== ""
                    ? String(invoiceNumber)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickPayNow failed:", e);
    }
};

window.trackCustomerClickSubmitPayment = function (cardNumber, payslipName) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack("Customers - Click Submit Payment", {
            card_number:
                cardNumber != null && String(cardNumber) !== ""
                    ? String(cardNumber)
                    : null,
            payslip_name:
                payslipName != null && String(payslipName) !== ""
                    ? String(payslipName)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickSubmitPayment failed:", e);
    }
};

window.trackCustomerClickSubmitPaymentSuccess = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            "Customers - Click Submit Payment Success",
            {}
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error(
            "[Amplitude] trackCustomerClickSubmitPaymentSuccess failed:",
            e
        );
    }
};

window.trackCustomerClickSubmitPaymentFail = function (errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            "Customers - Click Submit Payment Fail",
            {
                error_code:
                    errorCode != null && String(errorCode) !== ""
                        ? String(errorCode)
                        : null,
                error_message:
                    errorMessage != null && String(errorMessage) !== ""
                        ? String(errorMessage)
                        : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error("[Amplitude] trackCustomerClickSubmitPaymentFail failed:", e);
    }
};

// Pay Now click tracking (Outstanding invoices table)
document.addEventListener("click", function (e) {
    const btn = e.target.closest('button[data-amplitude-pay-now="1"]');
    if (!btn || !window.amplitudeTracker) return;

    window.trackCustomerClickPayNow?.(btn.dataset.amplitudeInvoiceNumber);
});

// Submit Payment click tracking (Payment modal)
document.addEventListener("click", function (e) {
    const btn = e.target.closest("#outstanding-payment-submit");
    if (!btn || !window.amplitudeTracker) return;

    const paymentType = document.querySelector(
        'input[name="payment_type"]:checked'
    )?.value;

    let cardNumber = null;
    if (paymentType === "CARD") {
        cardNumber = document
            .querySelector("#payment_id option:checked")
            ?.textContent?.trim();
    }

    let payslipName = null;
    if (paymentType === "CORPORATE") {
        const fileInputs = document.querySelectorAll(
            'input.payment_slip[type="file"]'
        );
        const names = Array.from(fileInputs)
            .map((i) => i.files && i.files[0] ? i.files[0].name : null)
            .filter(Boolean);
        payslipName = names.length ? names.join(", ") : null;
    }

    window.trackCustomerClickSubmitPayment?.(cardNumber, payslipName);
});

/* ================= PREFERENCES (Customer Detail) EVENTS ================= */
window.trackCustomerClickUpdatePreferences = function (
    customerId,
    theme,
    notification,
    sideMenu,
    locationAccess
) {
    try {
        if (!window.amplitudeTracker) return;
        if (customerId == null || customerId === "") return;

        window.amplitudeTracker._safeTrack(
            "Customers - Click Update Preferences",
            {
                customer_id: String(customerId),
                theme: theme != null ? String(theme) : null,
                notification: notification != null ? String(notification) : null,
                side_menu: sideMenu != null ? String(sideMenu) : null,
                location_access:
                    locationAccess != null ? String(locationAccess) : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error(
            "[Amplitude] trackCustomerClickUpdatePreferences failed:",
            e
        );
    }
};

window.trackCustomerClickUpdatePreferencesSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker) return;
        if (customerId == null || customerId === "") return;

        window.amplitudeTracker._safeTrack(
            "Customers - Click Update Preferences Success",
            { customer_id: String(customerId) }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error(
            "[Amplitude] trackCustomerClickUpdatePreferencesSuccess failed:",
            e
        );
    }
};

window.trackCustomerClickUpdatePreferencesFail = function (
    errorCode,
    errorMessage
) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack(
            "Customers - Click Update Preferences Fail",
            {
                error_code:
                    errorCode != null && String(errorCode) !== ""
                        ? String(errorCode)
                        : null,
                error_message:
                    errorMessage != null && String(errorMessage) !== ""
                        ? String(errorMessage)
                        : null,
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error(
            "[Amplitude] trackCustomerClickUpdatePreferencesFail failed:",
            e
        );
    }
};

document.addEventListener("click", function (e) {
    const btn = e.target.closest("#customer-update-preferences-submit");
    if (!btn || !window.amplitudeTracker) return;

    const form = btn.closest("form");
    if (!form) return;

    const customerId = form.querySelector('input[name="user_id"]')?.value;
    const theme = form.querySelector("#theme")?.value ?? null;
    const notification = form.querySelector(
        'input[name="notification"]:checked'
    )?.value;
    const sideMenu = form.querySelector('input[name="side_menu"]:checked')?.value;

    const locationAccessRaw = form.querySelector(
        'input[name="geolocation_access"]'
    )?.value;
    const locationAccess =
        locationAccessRaw === "on" ? "Allowed" : "Not Allowed";

    window.trackCustomerClickUpdatePreferences?.(
        customerId,
        theme,
        notification,
        sideMenu,
        locationAccess
    );
});

/* ================= CUSTOMER LIFECYCLE (DELETE / RESTORE / PASSWORD / ETC.) ================= */

function _isCustomerAmplitudeEntity(entity) {
    const e = String(entity || '').toLowerCase();
    return e === 'customer' || e === 'customers';
}

window.trackCustomerDeleteCustomerClick = function (customerId, source) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Delete Customer', {
            customer_id: String(customerId),
            source: source || 'customer_table',
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerDeleteCustomerClick failed:', e);
    }
};

window.trackCustomerConfirmDeleteCustomer = function (customerId, deletionReason) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Confirm Delete Customer', {
            customer_id: String(customerId),
            deletion_reason:
                deletionReason != null && String(deletionReason) !== ''
                    ? String(deletionReason)
                    : null,
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmDeleteCustomer failed:', e);
    }
};

window.trackCustomerDeleteCustomerSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Delete Customer Success', {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerDeleteCustomerSuccess failed:', e);
    }
};

window.trackCustomerDeleteCustomerFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Delete Customer Fail', {
            customer_id: String(customerId),
            error_code:
                errorCode != null && String(errorCode) !== ''
                    ? String(errorCode)
                    : null,
            error_message:
                errorMessage != null && String(errorMessage) !== ''
                    ? String(errorMessage)
                    : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerDeleteCustomerFail failed:', e);
    }
};

window.trackCustomerCancelDeleteCustomer = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Cancel Delete Customer', {
            customer_id: String(customerId),
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelDeleteCustomer failed:', e);
    }
};

window.trackCustomerDeletePermanentlyCustomerClick = function (customerId, source, customerName) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Delete Permanently Customer', {
            customer_id: String(customerId),
            source: source || 'customer_deleted_list',
            customer_name: customerName != null ? String(customerName) : null,
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerDeletePermanentlyCustomerClick failed:', e);
    }
};

window.trackCustomerConfirmDeletePermanentlyCustomer = function (customerId, customerName) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Confirm Delete Permanently Customer', {
            customer_id: String(customerId),
            customer_name: customerName != null ? String(customerName) : null,
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmDeletePermanentlyCustomer failed:', e);
    }
};

window.trackCustomerDeletePermanentlyCustomerSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Confirm Delete Permanently Customer Success', {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerDeletePermanentlyCustomerSuccess failed:', e);
    }
};

window.trackCustomerDeletePermanentlyCustomerFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Confirm Delete Permanently Customer Fail', {
            customer_id: String(customerId),
            error_code: errorCode != null && String(errorCode) !== '' ? errorCode : null,
            error_message:
                errorMessage != null && String(errorMessage) !== '' ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerDeletePermanentlyCustomerFail failed:', e);
    }
};

window.trackCustomerCancelDeletePermanentlyCustomer = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Cancel Delete Permanently Customer', {
            customer_id: String(customerId),
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelDeletePermanentlyCustomer failed:', e);
    }
};

window.trackCustomerRestoreDeletedCustomerClick = function (customerId, customerName, source) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Restore Deleted Customer', {
            customer_id: String(customerId),
            customer_name: customerName != null ? String(customerName) : null,
            source: source || 'customer_deleted_list',
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerRestoreDeletedCustomerClick failed:', e);
    }
};

window.trackCustomerConfirmRestoreDeletedCustomer = function (customerId, customerName) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Confirm Restore Deleted Customer', {
            customer_id: String(customerId),
            customer_name: customerName != null ? String(customerName) : null,
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmRestoreDeletedCustomer failed:', e);
    }
};

window.trackCustomerRestoreDeletedCustomerSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Confirm Restore Deleted Customer Success', {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerRestoreDeletedCustomerSuccess failed:', e);
    }
};

window.trackCustomerRestoreDeletedCustomerFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Confirm Restore Deleted Customer Fail', {
            customer_id: String(customerId),
            error_code: errorCode != null && String(errorCode) !== '' ? errorCode : null,
            error_message:
                errorMessage != null && String(errorMessage) !== '' ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerRestoreDeletedCustomerFail failed:', e);
    }
};

window.trackCustomerCancelRestoreDeletedCustomer = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Cancel Restore Deleted Customer', {
            customer_id: customerId != null && customerId !== '' ? String(customerId) : null,
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelRestoreDeletedCustomer failed:', e);
    }
};

window.trackCustomerChangePasswordClick = function (customerId, tableName) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Change Password', {
            customer_id: String(customerId),
            table_name: tableName || 'customer-table',
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerChangePasswordClick failed:', e);
    }
};

window.trackCustomerUpdateChangePasswordClick = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Update Change Password', {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateChangePasswordClick failed:', e);
    }
};

window.trackCustomerUpdateChangePasswordSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Update Change Password Success', {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateChangePasswordSuccess failed:', e);
    }
};

window.trackCustomerUpdateChangePasswordFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Update Change Password Fail', {
            customer_id: String(customerId),
            error_code: errorCode != null && String(errorCode) !== '' ? errorCode : null,
            error_message:
                errorMessage != null && String(errorMessage) !== '' ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpdateChangePasswordFail failed:', e);
    }
};

window.trackCustomerCancelChangePassword = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Cancel Change Password', {
            customer_id: String(customerId),
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelChangePassword failed:', e);
    }
};

window.trackCustomerTerminateCustomerClick = function (customerId, source, tableName) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Terminate Customer', {
            customer_id: String(customerId),
            source: source || 'customer_dropdown',
            table_name: tableName || 'customer-table',
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerTerminateCustomerClick failed:', e);
    }
};

window.trackCustomerConfirmTerminateCustomer = function (customerId, terminationReason) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Confirm Terminate Customer', {
            customer_id: String(customerId),
            termination_reason: terminationReason != null ? String(terminationReason) : null,
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmTerminateCustomer failed:', e);
    }
};

window.trackCustomerCancelTerminateCustomer = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Cancel Terminate Customer', {
            customer_id: String(customerId),
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelTerminateCustomer failed:', e);
    }
};

window.trackCustomerTerminateCustomerSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Terminate Customer Success', {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerTerminateCustomerSuccess failed:', e);
    }
};

window.trackCustomerTerminateCustomerFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Terminate Customer Fail', {
            customer_id: String(customerId),
            error_code: errorCode != null && String(errorCode) !== '' ? errorCode : null,
            error_message:
                errorMessage != null && String(errorMessage) !== '' ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerTerminateCustomerFail failed:', e);
    }
};

window.trackCustomerVerifyUserClick = function (customerId, source) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('Customers - Click Verify User', {
            customer_id:
                customerId != null && customerId !== '' ? String(customerId) : null,
            source: source || 'customer_dropdown',
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerVerifyUserClick failed:', e);
    }
};

window.trackCustomerConfirmVerifyUser = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Confirm Verify User', {
            customer_id: String(customerId),
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmVerifyUser failed:', e);
    }
};

window.trackCustomerVerifyUserSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Verify User Success', {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerVerifyUserSuccess failed:', e);
    }
};

window.trackCustomerVerifyUserFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Verify User Fail', {
            customer_id: String(customerId),
            error_code: errorCode != null && String(errorCode) !== '' ? errorCode : null,
            error_message:
                errorMessage != null && String(errorMessage) !== '' ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerVerifyUserFail failed:', e);
    }
};

window.trackCustomerCancelVerifyUser = function (source) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('Customers - Click Cancel Verify User', {
            source: source || 'customer_dropdown',
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelVerifyUser failed:', e);
    }
};

window.trackCustomerUpgradeToCorporateClick = function (source) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('Customers - Click Upgrade To Corporate', {
            source: source || 'customer_dropdown',
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpgradeToCorporateClick failed:', e);
    }
};

window.trackCustomerConfirmUpgradeToCorporate = function (customerId, columnName) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Click Confirm Upgrade To Corporate', {
            customer_id: String(customerId),
            column_name: columnName != null ? String(columnName) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerConfirmUpgradeToCorporate failed:', e);
    }
};

window.trackCustomerUpgradeToCorporateSuccess = function (customerId) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Upgrade To Corporate Success', {
            customer_id: String(customerId),
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpgradeToCorporateSuccess failed:', e);
    }
};

window.trackCustomerUpgradeToCorporateFail = function (customerId, errorCode, errorMessage) {
    try {
        if (!window.amplitudeTracker || customerId == null || customerId === '') return;
        window.amplitudeTracker._safeTrack('Customers - Upgrade To Corporate Fail', {
            customer_id: String(customerId),
            error_code: errorCode != null && String(errorCode) !== '' ? errorCode : null,
            error_message:
                errorMessage != null && String(errorMessage) !== '' ? String(errorMessage) : null,
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackCustomerUpgradeToCorporateFail failed:', e);
    }
};

window.trackCustomerCancelUpgradeToCorporate = function (customerId) {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('Customers - Click Cancel Upgrade To Corporate', {
            customer_id:
                customerId != null && customerId !== '' ? String(customerId) : null,
        });
    } catch (e) {
        console.error('[Amplitude] trackCustomerCancelUpgradeToCorporate failed:', e);
    }
};

/* ================= CUSTOMER MULTI-STEP EVENTS ================= */
window.trackCustomerNextPersonalInformationSuccess = function (
    email,
    erpAccountNumber
) {
    if (!window.amplitudeTracker) return;
    window.amplitudeTracker._safeTrack(
        'Customers - Click Next Personal Information Success',
        {
            email: email ?? null,
            erp_account_number: erpAccountNumber ?? null,
        }
    );
    window.amplitudeTracker.flush();
};

window.trackCustomerNextPersonalInformationFail = function (
    erpAccountNumber,
    errorCode,
    errorMessage
) {
    if (!window.amplitudeTracker) return;
    window.amplitudeTracker._safeTrack(
        'Customers - Click Next Personal Information Fail',
        {
            erp_account_number: erpAccountNumber ?? null,
            error_code: errorCode ?? null,
            error_message: errorMessage ?? null,
        }
    );
    window.amplitudeTracker.flush();
};

window.trackCustomerConfirmCreateIndividualSuccess = function (
    email,
    erpAccountNumber
) {
    if (!window.amplitudeTracker) return;
    window.amplitudeTracker._safeTrack(
        'Customers - Create Individual Customer Success',
        {
            email: email ?? null,
            erp_account_number: erpAccountNumber ?? null,
        }
    );
    window.amplitudeTracker.flush();
};

window.trackCustomerConfirmCreateIndividualFail = function (
    erpAccountNumber,
    errorCode,
    errorMessage
) {
    if (!window.amplitudeTracker) return;
    window.amplitudeTracker._safeTrack(
        'Customers - Create Individual Customer Fail',
        {
            erp_account_number: erpAccountNumber ?? null,
            error_code: errorCode ?? null,
            error_message: errorMessage ?? null,
        }
    );
    window.amplitudeTracker.flush();
};

/** After redirect from company create (session flash). */
window.trackGlobalCompanyCreateResult = function (
    status,
    companyId = null,
    errorMessage = null,
    errorCode = null
) {
    try {
        if (!window.amplitudeTracker) return;
        if (!status || (status !== 'success' && status !== 'fail')) return;

        const id =
            companyId != null && companyId !== '' ? companyId : null;

        if (status === 'success') {
            const tracked = window.amplitudeTracker._safeTrack(
                'Companies - Create Company Success',
                {
                    entity: 'company',
                    entity_id: id,
                }
            );
            if (tracked) window.amplitudeTracker.flush();
            return;
        }

        const tracked = window.amplitudeTracker._safeTrack(
            'Companies - Create Company Fail',
            {
                entity: 'company',
                entity_id: id,
                error_code: errorCode ?? null,
                error_message: errorMessage ?? null,
            }
        );
        if (tracked) window.amplitudeTracker.flush();
    } catch (error) {
        console.error('[Amplitude] Error in trackGlobalCompanyCreateResult:', error);
    }
};

document.addEventListener('DOMContentLoaded', function () {
    const entity = window.CREATE_ENTITY || null;
    if (!entity) return;

    // Company create form uses explicit onclick (trackGlobalFormConfirm / trackGlobalFormCancel).
    // Attaching here would duplicate those events.
    if (entity === 'company') return;

    const form = document.getElementById('create-form');
    if (!form) return;

    // Confirm Create
    form.addEventListener('submit', function () {
        window.trackGlobalCreateClick?.(entity);
    });

    // Cancel Create
    document.querySelectorAll('.btn-cancel').forEach(btn => {
        btn.addEventListener('click', function () {
            window.trackGlobalCreateCancel?.(entity);
        });
    });
});


/*================== RATE MANAGEMENT HELPERS =========================== */
/**
 * OWR calculator result (validation error, same coordinates, or successful calculation).
 * @param {'success'|'fail'} status
 * @param {object|string|null} payload - object with properties, or legacy rate_id string
 */
window.trackGlobalOwrCalculateResult = function (status, payload = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const ok = String(status).toLowerCase() === 'success';
        const p =
            typeof payload === 'object' && payload !== null && !Array.isArray(payload)
                ? payload
                : { rate_id: payload };

        const base = {
            rate_id:
                p.rate_id != null && p.rate_id !== ''
                    ? String(p.rate_id)
                    : null,
            source: 'owr_calculator'
        };

        if (ok) {
            const km = p.total_kilometer;
            base.total_kilometer =
                km != null && km !== '' && !Number.isNaN(Number(km))
                    ? Number(km)
                    : km != null
                      ? String(km)
                      : null;
            base.owr_rate =
                p.owr_rate != null && p.owr_rate !== '' ? p.owr_rate : null;
        } else {
            base.error_code =
                p.error_code != null && p.error_code !== ''
                    ? String(p.error_code)
                    : null;
            base.error_message =
                p.error_message != null && p.error_message !== ''
                    ? String(p.error_message)
                    : null;
        }

        window.amplitudeTracker._safeTrack(
            `Rates - Calculate OWR Rate ${ok ? 'Success' : 'Fail'}`,
            base
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalOwrCalculateResult:', e);
    }
};

/**
 * Calculate button on OWR page — includes selected location labels.
 */
window.trackGlobalOwrCalculateButtonClick = function () {
    try {
        const pu = document.getElementById('pickup_at');
        const ret = document.getElementById('return_at');
        const pickLabel =
            pu && pu.selectedOptions && pu.selectedOptions[0]
                ? String(pu.selectedOptions[0].textContent || '')
                      .replace(/\s+/g, ' ')
                      .trim()
                : '';
        const retLabel =
            ret && ret.selectedOptions && ret.selectedOptions[0]
                ? String(ret.selectedOptions[0].textContent || '')
                      .replace(/\s+/g, ' ')
                      .trim()
                : '';
        window.trackGlobalRateManagementClick('Calculate', {
            rate_id: null,
            pick_up_location: pickLabel || null,
            return_location: retLabel || null
        });
    } catch (e) {
        console.error('[Amplitude] trackGlobalOwrCalculateButtonClick:', e);
    }
};

/** Primary actions not covered by data-amplitude-action (e.g. OWR Calculate / Reset). */
window.trackGlobalRateManagementClick = function (actionLabel, extra = {}) {
    try {
        if (!window.amplitudeTracker || !actionLabel) return;
        const moduleName =
            typeof getCurrentModule === 'function' ? getCurrentModule() : 'Unknown';
        const menuName =
            typeof getCurrentMenu === 'function' ? getCurrentMenu() : 'Unknown';
        const menuPart =
            menuName && menuName !== 'Unknown' ? ` ${menuName}` : '';
        const {
            rate_id: rateIdFromExtra,
            pick_up_location: pickUpLoc,
            return_location: returnLoc,
            ...restExtra
        } = extra;
        const payload = {
            rate_id:
                rateIdFromExtra != null && rateIdFromExtra !== ''
                    ? String(rateIdFromExtra)
                    : null,
            source: 'rate_management',
            ...restExtra
        };
        if (pickUpLoc != null && pickUpLoc !== '') {
            payload.pick_up_location = String(pickUpLoc);
        } else {
            payload.pick_up_location = null;
        }
        if (returnLoc != null && returnLoc !== '') {
            payload.return_location = String(returnLoc);
        } else {
            payload.return_location = null;
        }
        window.amplitudeTracker._safeTrack(
            `${moduleName} - Click ${String(actionLabel)}${menuPart}`,
            payload
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalRateManagementClick:', e);
    }
};


/*================== GLOBAL CLICK VIEW =========================== */
window.trackGlobalViewClick = function (entity, entityId) {
    if (!window.amplitudeTracker) return;

    const moduleName = getCurrentModule();
    let menuName   = getCurrentMenu();
    const entityLower = String(entity || '').toLowerCase();

    // Admin detail views should keep "Customers" menu label for compatibility.
    if (
        moduleName === 'System' &&
        entityLower === 'admin' &&
        String(menuName || '').toLowerCase() === 'admin management'
    ) {
        menuName = 'Customers';
    }

    amplitudeTracker.track(
        `${moduleName} - Click View Detail ${menuName}`,
        {
            source: `${entity}_list`,
            entity: entity,
            entity_id: entityId,
            ...(entityLower === 'rate'
                ? { rate_id: entityId != null ? String(entityId) : null }
                : {})
        }
    );
};


/* ================= GLOBAL CLICK BACK =========================== */
window.trackGlobalViewBackClick = function (entity, entityId) {
    try {
        if (!window.amplitudeTracker) {
            console.warn('Amplitude tracker not initialized');
            return true; // allow navigation
        }

        const moduleName = typeof getCurrentModule === 'function' ? getCurrentModule() : 'UnknownModule';
        const entityLower = String(entity || '').toLowerCase();
        const pathLower = (
            typeof location !== 'undefined' ? location.pathname || '' : ''
        ).toLowerCase();
        let menuName;
        if (entityLower === 'promotion') {
            menuName = CONFIG.MENU_MAPPINGS.promotion;
        } else if (entityLower === 'coupon') {
            menuName = 'Coupon';
        } else if (entityLower === 'corporate') {
            if (pathLower.startsWith('/corporate/profiles')) {
                menuName = 'Corporate Info Update';
            } else if (pathLower.startsWith('/corporate/inquiries')) {
                menuName = 'Corporate';
            } else {
                menuName = 'Corporate';
            }
        } else if (typeof capitalize === 'function') {
            menuName = capitalize(entity);
        } else {
            menuName = entity;
        }

        if (typeof amplitudeTracker.track === 'function') {
            amplitudeTracker.track(
                `${moduleName} - Click Back View Detail ${menuName}`,
                {
                    source: `${entity}_detail_view`,
                    entity,
                    entity_id: entityId,
                    ...(entityLower === 'rate'
                        ? { rate_id: entityId != null ? String(entityId) : null }
                        : {})
                }
            );
        }

    } catch (err) {
        console.error('Error in trackGlobalViewBackClick:', err);
    }

    return true; // always allow navigation
};

/**
 * Push notification publish (confirm dialog) — uses notification_id via _trackAction.
 */
window.trackGlobalNotificationPublishClick = function (notificationId) {
    try {
        if (!window.amplitudeTracker || notificationId == null) return;
        const tracker = window.amplitudeTracker._trackAction(
            'publish',
            'notification',
            notificationId,
            { actionLabel: 'Publish' }
        );
        tracker.click();
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalNotificationPublishClick:', e);
    }
};

window.trackGlobalNotificationPublishConfirm = function (notificationId) {
    try {
        if (!window.amplitudeTracker || notificationId == null) return;
        const tracker = window.amplitudeTracker._trackAction(
            'publish',
            'notification',
            notificationId,
            { actionLabel: 'Publish' }
        );
        tracker.confirm();
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalNotificationPublishConfirm:', e);
    }
};

window.trackGlobalNotificationPublishCancel = function (notificationId) {
    try {
        if (!window.amplitudeTracker || notificationId == null) return;
        window.amplitudeTracker._safeTrack(
            'System - Click Cancel Publish Notification',
            {
                source: 'notification_list',
                entity: 'notification',
                entity_id:
                    notificationId != null ? String(notificationId) : null
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalNotificationPublishCancel:', e);
    }
};

/**
 * Notification list — user dismissed delete confirmation (SweetAlert cancel / backdrop / Esc).
 * @param {string|number} notificationId
 * @param {string} [actionType]
 */
window.trackGlobalNotificationDeleteCancel = function (
    notificationId,
    actionType = 'soft_delete'
) {
    try {
        if (!window.amplitudeTracker || notificationId == null) return;
        window.amplitudeTracker._safeTrack(
            'System - Click Cancel Delete Notification',
            {
                source: 'notification_list',
                entity: 'notification',
                entity_id:
                    notificationId != null ? String(notificationId) : null,
                action_type: actionType
            }
        );
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackGlobalNotificationDeleteCancel:', e);
    }
};

/* ================= GLOBAL CLICK DELETE BINDING ================= */

/**
 * Delegated delete click — only `[name="form_delete"]`.
 * Do NOT bind `[name="form_permanently_delete"]` here: `plugin/alert.js` already
 * calls `trackGlobalDeleteClick` for permanent delete (with e.g. `acriss_code` ctx).
 * Binding both caused duplicate `Rates - Click Delete Permanently *` events.
 */
document.addEventListener('click', function (e) {
    const btn = e.target.closest('[name="form_delete"]');
    if (!btn || !window.amplitudeTracker) return;

    const entity     = btn.dataset.amplitudeEntity;
    const entityId   = btn.dataset.amplitudeEntityId;
    const actionType =
        btn.dataset.amplitudeDeleteType || 'soft_delete';

    if (!entity || !entityId) return;

    if (_isCustomerAmplitudeEntity(entity) && actionType === 'permanent_delete') {
        window.trackCustomerDeletePermanentlyCustomerClick?.(
            entityId,
            btn.dataset.amplitudeSource || 'customer_deleted_list',
            btn.dataset.amplitudeCustomerName || null
        );
        return;
    }

    trackGlobalDeleteClick(entity, entityId, actionType);
});



/* ================= CUSTOMER DETAIL TAB BINDING ================= */
document.addEventListener('click', function (e) {
    const tabLink = e.target.closest('.detail-sidebar a[data-amplitude-tab]');
    if (!tabLink || !window.amplitudeTracker) return;

    // "Disabled" links are still anchors; skip tracking them.
    if (tabLink.classList && tabLink.classList.contains('disabled')) return;
    if (typeof tabLink.className === 'string' && tabLink.className.includes('disabled')) return;

    const tabLabel = tabLink.dataset.amplitudeTab;
    if (!tabLabel) return;

    const source = tabLink.dataset.amplitudeSource || tabLabel;
    const customerId = tabLink.dataset.amplitudeCustomerId || null;

    const includeCompany =
        tabLink.dataset.amplitudeIncludeCompany === '1' ||
        tabLink.dataset.amplitudeIncludeCompany === 'true';

    const companyName = tabLink.dataset.amplitudeCompanyName || null;
    const companyId = tabLink.dataset.amplitudeCompanyId || null;

    const props = {
        source,
        customer_id: customerId,
    };

    if (includeCompany) {
        props.company_name = companyName || null;
        props.company_id = companyId || null;
    }

    window.amplitudeTracker._safeTrack(
        `Customers - Click ${tabLabel}`,
        props
    );
    window.amplitudeTracker.flush?.();
});

/* ================= CUSTOMER OVERVIEW BUTTON BINDING ================= */
document.addEventListener('click', function (e) {
    const overviewLink = e.target.closest(
        '[data-amplitude-overview-action]'
    );
    if (!overviewLink || !window.amplitudeTracker) return;

    const action = overviewLink.dataset.amplitudeOverviewAction;
    if (!action) return;

    const source = overviewLink.dataset.amplitudeOverviewSource || 'overview';
    const customerId = overviewLink.dataset.amplitudeOverviewCustomerId || null;

    const props = {
        source,
        customer_id: customerId,
    };

    const companyName = overviewLink.dataset.amplitudeOverviewCompanyName;
    const companyId = overviewLink.dataset.amplitudeOverviewCompanyId;

    if (companyName != null && companyName !== '') {
        props.company_name = companyName;
    }
    if (companyId != null && companyId !== '') {
        props.company_id = companyId;
    }

    window.amplitudeTracker._safeTrack(
        `Customers - Click ${action}`,
        props
    );
    window.amplitudeTracker.flush?.();
});
/* ================= SYSTEM NAV BINDING ================= */
document.addEventListener('click', function (e) {
    const toggle = e.target.closest('[data-amplitude-system-nav="access"]');
    if (!toggle || !window.amplitudeTracker) return;
    window.trackSystemClickAccess?.();
});

/* ================= GLOBAL INSTANCE ================= */
window.amplitudeTracker = new AmplitudeTracker();

document.addEventListener('DOMContentLoaded', () => {
    window.amplitudeTracker.init();
});


/* =====================================================
   Sidebar-based Module Detection
   ===================================================== */
function findNavTitle(element) {
    let current = element.closest('li');
    if (!current) return null;

    let prev = current.previousElementSibling;
    while (prev) {
        if (prev.classList.contains('nav-title')) return prev.textContent.trim();
        prev = prev.previousElementSibling;
    }

    // fallback
    const navGroup = element.closest('.nav-group-items');
    if (navGroup) {
        const group = navGroup.closest('.nav-group');
        if (group) {
            let sib = group.previousElementSibling;
            while (sib) {
                if (sib.classList.contains('nav-title')) return sib.textContent.trim();
                sib = sib.previousElementSibling;
            }
        }
    }

    return null;
}

function getCurrentModule() {
    const MODULE_ALIASES = {
        'Invoice Management': 'Invoices',
        'Location Management': 'Locations',
        'Location': 'Locations',
        'Admin Management': 'System',
        'Outstanding Invoice': 'Outstanding',
        'Rental': 'Outstanding',
        'Recharge/Refund/Other': 'Outstanding',
        'Point': 'Membership',
        'Points Management': 'Membership',
        'Customer Management': 'Customers',
        'Vehicle Management': 'Vehicles',
        // Sidebar subsection titles under Vehicle Management (avoid "Attributes - Click … Attributes")
        'Makes': 'Vehicles',
        'Make': 'Vehicles',
        'Vehicle Make': 'Vehicles',
        'Models': 'Vehicles',
        'Model': 'Vehicles',
        'Vehicle Model': 'Vehicles',
        'Attributes': 'Vehicles',
        'Attribute': 'Vehicles',
        'Vehicle Attribute': 'Vehicles',
        'Acriss Management': 'Rates',
        'Exchange Rate Management': 'Rates',
        'Rate Management': 'Rates',
        'Reviewed Reservations': 'Reservations',
        // Blade often uses @section('title', 'Inquiry'); Amplitude prefix matches sidebar nav title
        Inquiry: 'Inquiries',
        // System section (Command Center, Settings, Notification, etc.) — one Amplitude module prefix
        Notification: 'System',
        Settings: 'System',
        'Command Center': 'System',
        // Blade @section('title', 'Company') and legacy labels → Amplitude prefix
        'Company': 'Companies',
    };

    const normalizeModule = (name) => MODULE_ALIASES[name] || name;

    // Explicit override from backend
    if (window.CURRENT_MODULE) {
        return normalizeModule(window.CURRENT_MODULE);
    }

    const pathname =
        typeof location !== 'undefined' ? location.pathname.toLowerCase() : '';

    // URL: Rate Management module (acriss / rates / exchange / OWR)
    if (pathname.includes('ratemanagement')) {
        return normalizeModule('Rate Management');
    }

    // System area: Command Center, Settings, Notification, Access (admin), Roles
    if (
        pathname.includes('/notification') ||
        pathname.includes('commandcenter') ||
        pathname.startsWith('/setting') ||
        pathname.includes('adminmanagement') ||
        pathname === '/role' ||
        pathname.startsWith('/role/')
    ) {
        return 'System';
    }

    // Contact + Corporate inquiry UIs share sidebar nav title "Inquiries"
    if (
        pathname === '/enquiry' ||
        pathname.startsWith('/enquiry/') ||
        pathname.startsWith('/corporate/')
    ) {
        return 'Inquiries';
    }

    // Coupon CRUD lives under /coupon; Amplitude events use the same module prefix as promotion codes
    if (pathname === '/coupon' || pathname.startsWith('/coupon/')) {
        return 'Promotion';
    }

    // URL: all Vehicle Management / our-vehicle / vehicle.* admin routes share module "Vehicles"
    if (
        pathname.includes('vehiclemanagement') ||
        pathname.includes('our-vehicle') ||
        /^\/vehicle\//.test(pathname)
    ) {
        return 'Vehicles';
    }

    // Find active nav link
    const activeLink =
        document.querySelector('.nav-link.active') ||
        document.querySelector('.nav-item.active a') ||
        document.querySelector('.nav-item.active');

    if (activeLink) {
        // Try to resolve via nav title (module)
        const moduleFromNav = findNavTitle(activeLink);
        if (moduleFromNav) return normalizeModule(moduleFromNav);

        // Dashboard usually lives alone (no nav-title)
        const label =
            activeLink.textContent ||
            activeLink.getAttribute('title') ||
            '';

        if (/dashboard/i.test(label)) {
            return 'Dashboard';
        }
    }

    //  URL-based dashboard detection
    if (
        location.pathname === '/' ||
        location.pathname === '/dashboard'
    ) {
        return 'Dashboard';
    }

    // Company module routes (title is often singular "Company" from Blade)
    if (/\/companymanagement/i.test(location.pathname)) {
        return 'Companies';
    }

    //  Clean document title fallback
    if (document.title) {
        return normalizeModule(document.title.split('|')[0].trim());
    }

    return 'Unknown Module';
}



/* ================= MENU DETECTION ================= */
function getCurrentMenu() {
    const path = window.location.pathname.toLowerCase();

    // Admin Management pages should use "Customers" in event names.
    if (path.includes('adminmanagement')) {
        return 'Customers';
    }

    if (path.includes('/notification')) {
        return 'Notification';
    }

    // Corporate: profiles = "Corporate Info Update", inquiries list = "Corporate" (avoid generic path.includes('corporate') → "Corporate")
    if (path.startsWith('/corporate/profiles')) {
        return 'Corporate Info Update';
    }
    if (path.startsWith('/corporate/inquiries')) {
        return 'Corporate';
    }

    // Enquiry module (Contact inquiries)
    if (path === '/enquiry' || path.startsWith('/enquiry/')) {
        return 'Contact';
    }

    // Coupon pages: event names like "Promotion - Click Confirm Update Coupon"
    if (path === '/coupon' || path.startsWith('/coupon/')) {
        return 'Coupon';
    }

    // URL-first for vehicle areas: sidebar labels are often short duplicates ("Model", "Attributes")
    // which produced events like "Model - Click Update Model" instead of
    // "Vehicles - Click Update Vehicle Model".
    const vehicleAreaPath =
        path.includes('vehiclemanagement') ||
        path.includes('our-vehicle') ||
        /^\/vehicle\//.test(path);

    if (vehicleAreaPath) {
        for (const key in CONFIG.MENU_MAPPINGS) {
            if (path.includes(key)) {
                return CONFIG.MENU_MAPPINGS[key];
            }
        }
    }

    // URL-first for Rate Management sub-pages (stable menu names for events)
    if (path.includes('ratemanagement')) {
        for (const key in CONFIG.MENU_MAPPINGS) {
            if (path.includes(key)) {
                return CONFIG.MENU_MAPPINGS[key];
            }
        }
    }

    // Sidebar active link (deepest)
    let activeLink =
        document.querySelector('#primary-sidebar li.active a.nav-link')
        || document.querySelector('#primary-sidebar a.nav-link.active');

    if (activeLink) {
        let text = activeLink.textContent
            .replace(/\(\d+\)/g, '')
            .replace(/[0-9]/g, '')
            .replace(/\s+/g, ' ')
            .trim();

        const lower = text.toLowerCase();
        for (const key in CONFIG.MENU_MAPPINGS) {
            if (lower.includes(key)) {
                return CONFIG.MENU_MAPPINGS[key];
            }
        }

        if (text) return text;
    }

    // URL fallback (VERY IMPORTANT)
    for (const key in CONFIG.MENU_MAPPINGS) {
        if (path.includes(key)) {
            return CONFIG.MENU_MAPPINGS[key];
        }
    }

    // Final fallback
    console.warn('[Amplitude] Menu fallback hit:', path);
    return 'Unknown';
}

/* ================= CUSTOMER DETAIL TAB HELPERS ================= */
function getCurrentCustomerDetailTab() {
    // In customer detail pages, `.detail-sidebar` links are disabled on the active tab.
    // We use that state to infer which sub-tab is currently displayed.
    const tabLink = document.querySelector(
        '.detail-sidebar a[data-amplitude-tab].disabled'
    );

    if (tabLink && tabLink.dataset && tabLink.dataset.amplitudeTab) {
        return tabLink.dataset.amplitudeTab;
    }

    // Fallback: some templates may render disabled with only partial classnames.
    const anyTabLink = document.querySelector(
        '.detail-sidebar a[data-amplitude-tab]'
    );
    if (!anyTabLink) return null;

    // Try to find any tab link whose class includes "disabled" substring.
    const candidates = Array.from(
        document.querySelectorAll('.detail-sidebar a[data-amplitude-tab]')
    );
    const disabledCandidate = candidates.find((el) => {
        const cls = String(el.getAttribute('class') || '');
        return cls.includes('disabled');
    });

    if (disabledCandidate?.dataset?.amplitudeTab) {
        return disabledCandidate.dataset.amplitudeTab;
    }

    return null;
}



/* =====================================================
   Table Context Helper
   ===================================================== */
function resolveTableContext(element) {
    if (!element) return 'Unknown Table';

    const tableEl = element.closest('table');
    if (tableEl?.id) return tableEl.id;

    const namedEl = element.closest('[data-table-name]');
    if (namedEl?.dataset?.tableName) return namedEl.dataset.tableName;

    const card = element.closest('.card');
    if (card) {
        const title = card.querySelector('.card-title');
        if (title?.textContent.trim()) return title.textContent.trim();
    }

    return 'Unknown Table';
}

/* ================= EXISTING EVENT BINDINGS ================= */
document.addEventListener('click', e => {
    const btn = e.target.closest('.buttons-excel,.buttons-csv,.buttons-pdf,.buttons-print');
    if (btn) {
        const tableName = resolveTableContext(btn);
        const type = btn.classList.contains('buttons-excel') ? 'excel' :
                     btn.classList.contains('buttons-csv') ? 'csv' :
                     btn.classList.contains('buttons-pdf') ? 'pdf' :
                     'print';
        window.amplitudeTracker.table.export(tableName, type);
    }
});

document.addEventListener('click', e => {
    const btn = e.target.closest('.lock-action-btn');
    if (btn) {
        const tableName = resolveTableContext(btn);
        window.amplitudeTracker.table.clickAction(tableName);
    }
});

document.addEventListener('keydown', e => {
    const input = e.target.closest('tfoot input, thead input');
    if (!input) return;

    if (e.key !== 'Enter') return;
    if (!input.value.trim()) return;

    window.amplitudeTracker._hasUserSearched = true;

    const tableEl = input.closest('table');
    let column = 'unknown';

    if (tableEl) {
        const ths = tableEl.querySelectorAll('thead th');
        const tfootTh = input.closest('th');
        if (tfootTh) {
            const index = Array.from(tfootTh.parentElement.children).indexOf(tfootTh);
            if (ths[index]) {
                column = ths[index].textContent.trim();
            }
        }
    }

    const tableName = resolveTableContext(input);

    window.amplitudeTracker.table.search(
        tableName,
        column,
        input.value.trim()
    );
});


document.addEventListener('change', e => {
    const el = e.target.closest('thead select');
    if (el && el.value) {
        const tableName = resolveTableContext(el);
        window.amplitudeTracker.table.filter(tableName, el.name || 'unknown', el.value);
    }

    const select = e.target.closest('.dataTables_length select');
    if (select) {
        const tableName = resolveTableContext(select);
        window.amplitudeTracker.table.rowsPerPage(tableName, select.value);
    }
});

document.addEventListener('click', e => {
    const th = e.target.closest('th.sorting,th.sorting_asc,th.sorting_desc');

    if (th) {
        const dir = th.classList.contains('sorting_asc') ? 'asc' :
                    th.classList.contains('sorting_desc') ? 'desc' :
                    'none';

        const tableName = 'customer-table'; // force value

        // Map column name if needed
        const rawColumnName = th.dataset.column || th.textContent.trim();

        const columnMap = {
            'Contact Number': 'ERP Account Number'
        };

        const columnName = columnMap[rawColumnName] || rawColumnName;

        window.amplitudeTracker.table.sort(tableName, dir, columnName);
    }


    const btn = e.target.closest('.dataTables_paginate a.previous,.dataTables_paginate a.next');
    if (btn && !btn.classList.contains('disabled')) {
        const tableName = resolveTableContext(btn);
        window.amplitudeTracker.table.pageChange(tableName, btn.classList.contains('previous') ? 'previous' : 'next');
    }

    const pageBtn = e.target.closest('.dataTables_paginate a:not(.previous):not(.next):not(.disabled)');
    if (pageBtn && /^\d+$/.test(pageBtn.textContent)) {
        const tableName = resolveTableContext(pageBtn);
        window.amplitudeTracker.table.choosePageNo(tableName, pageBtn.textContent);
    }
});

/* ================= SEARCH SUCCESS / FAIL ================= */
function initGlobalDataTableSearchTracking() {
    // Check if jQuery is available, otherwise use vanilla JS approach
    if (typeof window.$ !== 'undefined' && typeof window.$.fn.dataTable !== 'undefined') {
        // jQuery approach (legacy support)
        $(document).on('draw.dt', '.dataTable', function () {
            if (!window.amplitudeTracker?._hasUserSearched) return;

            try {
                const api = $(this).DataTable();
                const info = api.page.info();
                if (!info) return;

                const columnSearches = api.columns().search().toArray();
                const hasRealSearch = columnSearches.some(v => v && v.trim().length > 0);
                if (!hasRealSearch) return;

                const signature = columnSearches.join('|');
                if (signature === window.amplitudeTracker._lastSearchSignature) return;

                window.amplitudeTracker._lastSearchSignature = signature;

                const tableName = resolveTableContext(this);
                window.amplitudeTracker.searchResult.success(info.recordsDisplay);
            } catch (error) {
                console.error('[Amplitude] Error in DataTable draw handler:', error);
            }
        });

        $(document).on('error.dt', '.dataTable', function (e, settings, tech, msg) {
            try {
                const tableName = resolveTableContext(this);
                window.amplitudeTracker?.searchResult.fail(tech, msg);
            } catch (error) {
                console.error('[Amplitude] Error in DataTable error handler:', error);
            }
        });
    } else {
        // Vanilla JavaScript approach (modern, no jQuery dependency)
        document.addEventListener('DOMContentLoaded', () => {
            const tables = document.querySelectorAll('.dataTable');
            
            tables.forEach(table => {
                // Check if DataTable API is available
                if (table.DataTable) {
                    try {
                        const dt = table.DataTable();
                        
                        dt.on('draw', () => {
                            if (!window.amplitudeTracker?._hasUserSearched) return;

                            try {
                                const info = dt.page.info();
                                if (!info) return;

                                const columnSearches = dt.columns().search().toArray();
                                const hasRealSearch = columnSearches.some(v => v && v.trim().length > 0);
                                if (!hasRealSearch) return;

                                const signature = columnSearches.join('|');
                                if (signature === window.amplitudeTracker._lastSearchSignature) return;

                                window.amplitudeTracker._lastSearchSignature = signature;

                                const tableName = resolveTableContext(table);
                                window.amplitudeTracker.searchResult.success(info.recordsDisplay);
                            } catch (error) {
                                console.error('[Amplitude] Error in DataTable draw handler:', error);
                            }
                        });

                        dt.on('error', (e, settings, tech, msg) => {
                            try {
                                const tableName = resolveTableContext(table);
                                window.amplitudeTracker?.searchResult.fail(tech, msg);
                            } catch (error) {
                                console.error('[Amplitude] Error in DataTable error handler:', error);
                            }
                        });
                    } catch (error) {
                        console.warn('[Amplitude] Could not initialize DataTable tracking:', error);
                    }
                }
            });
        });
    }
}

document.addEventListener('DOMContentLoaded', initGlobalDataTableSearchTracking);

/* ================= HEADER PROFILE (avatar dropdown) ================= */
window.trackProfileClickProfile = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const p =
            props && typeof props === 'object' && !Array.isArray(props)
                ? props
                : {};
        window.amplitudeTracker._safeTrack('Profile - Click Profile', {
            customer_id:
                p.customer_id != null && String(p.customer_id).trim() !== ''
                    ? String(p.customer_id)
                    : null,
            source:
                p.source != null && String(p.source).trim() !== ''
                    ? String(p.source)
                    : 'header_avatar_dropdown',
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackProfileClickProfile failed:', e);
    }
};

window.trackProfileClickLogOut = function () {
    try {
        if (!window.amplitudeTracker) return;
        window.amplitudeTracker._safeTrack('Profile - Click Log Out', {
            source: 'header_avatar_dropdown',
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackProfileClickLogOut failed:', e);
    }
};

window.trackProfileLogOutSuccess = function (props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const p =
            props && typeof props === 'object' && !Array.isArray(props)
                ? props
                : {};
        window.amplitudeTracker._safeTrack('Profile - Log Out Success', {
            customer_id:
                p.customer_id != null && String(p.customer_id).trim() !== ''
                    ? String(p.customer_id)
                    : null,
            source:
                p.source != null && String(p.source).trim() !== ''
                    ? String(p.source)
                    : 'header_logout',
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackProfileLogOutSuccess failed:', e);
    }
};

window.trackProfileLogOutFail = function (errorCode, errorMessage, props = {}) {
    try {
        if (!window.amplitudeTracker) return;
        const p =
            props && typeof props === 'object' && !Array.isArray(props)
                ? props
                : {};
        window.amplitudeTracker._safeTrack('Profile - Log Out Fail', {
            error_code: errorCode != null ? String(errorCode) : null,
            error_message:
                errorMessage != null ? String(errorMessage) : null,
            source:
                p.source != null && String(p.source).trim() !== ''
                    ? String(p.source)
                    : 'header_logout',
        });
        window.amplitudeTracker.flush?.();
    } catch (e) {
        console.error('[Amplitude] trackProfileLogOutFail failed:', e);
    }
};

document.addEventListener('click', function (e) {
    const target = e.target.closest('[data-amplitude-profile-link]');
    if (!target || !window.amplitudeTracker) return;
    if (!target.closest('header.header')) return;

    const kind = target.getAttribute('data-amplitude-profile-link');
    if (kind === 'profile') {
        const cid = target.getAttribute('data-amplitude-customer-id');
        const src = target.getAttribute('data-amplitude-source');
        window.trackProfileClickProfile?.({
            customer_id: cid,
            source: src || 'header_avatar_dropdown',
        });
        return;
    }
    if (kind === 'logout') {
        try {
            sessionStorage.setItem('__amp_profile_logout_pending', '1');
        } catch (err) {
            /* ignore private mode etc. */
        }
        window.trackProfileClickLogOut?.();
    }
});

/*=========== DASHBOARD DROPDOWN ITEM CLICK ==============*/
document.addEventListener('click', function (e) {
    const item = e.target.closest('.dropdown-menu .dropdown-item');
    if (!item || !window.amplitudeTracker) return;

    // Safety: only dashboard
    if (!location.pathname.includes('dashboard')) return;

    const label = item.dataset.amplitudeLabel;
    if (!label) return;

    // Mark so generic handler skips it
    item.dataset._amplitudeHandled = 'dashboard';

    window.amplitudeTracker.track(
        `Dashboard - Click ${label}`,
        {
            page: 'dashboard',
            value: item.dataset.amplitudeValue
                ? String(item.dataset.amplitudeValue)
                : null
        }
    );
});

/*============= DROPDOWN ITEM CLICK ================ */
document.addEventListener('click', function (e) {
    const item = e.target.closest(
        '.dropdown-menu a, .dropdown-menu button, .dropdown-menu [role="menuitem"]'
    );

    if (!item || !window.amplitudeTracker) return;

    if (item.dataset.amplitudeSkipGenericDropdown === '1') return;

    // Skip dashboard events already handled
    if (item.dataset._amplitudeHandled === 'dashboard') return;

    const moduleName = getCurrentModule();

    let itemName =
        item.dataset.amplitudeItem ||
        item.textContent.replace(/\s+/g, ' ').trim() ||
        'Unknown Action';

    if (itemName === 'Create Promotions') {
        itemName = 'Create Promotion Code';
    }

    const tableName = resolveTableContext(item);

    window.amplitudeTracker.track(
        `${moduleName} - Click ${itemName}`,
        {
            source: 'datatable_dropdown',
            table: tableName,
        }
    );
});


/* ===== Entity Id replace ======= */
function buildEntityIdProperty(entity, entityId) {
    if (!entity || entityId == null) {
        return {};
    }

    const key = CONFIG.ENTITY_ID_MAP[String(entity).toLowerCase()] || 'entity_id';

    return {
        [key]: entityId
    };
}

/**
 * True when the user is in Vehicle admin (fleet, builders, our-vehicle, archived vehicle routes).
 */
function isVehicleManagementAmplitudeContext() {
    const path = typeof location !== 'undefined' ? location.pathname.toLowerCase() : '';
    return (
        path.includes('vehiclemanagement') ||
        path.includes('our-vehicle') ||
        /^\/vehicle\//.test(path)
    );
}

/**
 * Best-effort fleet vehicle id from URL (avoid /image/{id} etc.).
 */
function resolveContextVehicleIdFromPath(pathname) {
    if (!pathname) return null;
    const path = pathname.toLowerCase();
    if (!path.includes('vehiclemanagement')) return null;

    const m = (re) => {
        const x = pathname.match(re);
        return x ? String(x[1]) : null;
    };

    return (
        m(/\/vehiclemanagement\/edit\/(\d+)/i) ||
        m(/\/vehiclemanagement\/create\/(\d+)/i) ||
        m(/\/vehiclemanagement\/(\d+)\/clone/i) ||
        m(/\/vehiclemanagement\/rentchecklist\/(\d+)/i) ||
        m(/\/vehiclemanagement\/history\/(\d+)/i) ||
        m(/\/vehiclemanagement\/delete\/(\d+)/i) ||
        m(/\/vehiclemanagement\/hard-delete\/(\d+)/i) ||
        m(/^\/vehiclemanagement\/(\d+)\/?$/i)
    );
}

function resolveContextVehicleIdFromDom() {
    try {
        const el =
            document.querySelector('input#vehicle_id[name="vehicle_id"]') ||
            document.querySelector('input[name="vehicle_id"]');
        const v = el?.value;
        if (v != null && String(v).trim() !== '') {
            return String(v).trim();
        }
    } catch (e) {
        /* ignore */
    }
    return null;
}

/**
 * Fleet vehicle_id for vehicle-area events: explicit prop > URL > form hidden > entity vehicle row id.
 */
function resolveVehicleIdForVehicleAreaEvent(explicitVehicleId, entity, entityId) {
    if (explicitVehicleId != null && String(explicitVehicleId).trim() !== '') {
        return String(explicitVehicleId).trim();
    }
    const fromPath = resolveContextVehicleIdFromPath(
        typeof location !== 'undefined' ? location.pathname : ''
    );
    if (fromPath) return fromPath;
    const fromDom = resolveContextVehicleIdFromDom();
    if (fromDom) return fromDom;
    const e = String(entity || '').toLowerCase();
    if (e === 'vehicle' || e === 'vehicles') {
        if (entityId != null && String(entityId).trim() !== '') {
            return String(entityId).trim();
        }
    }
    return null;
}


// Make tracking functions globally available
window.trackReservationClickArchivedReservation = function (tableName, source, reservationId) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack('Reservations - Click Archived Reservation', {
        table_name: tableName || null,
        source: source || null,
        reservation_id: reservationId || null,
    });
};

window.trackReservationClickConfirmArchivedReservation = function (reservationId) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack('Reservations - Click Confirm Archived Reservation', {
        reservation_id: reservationId || null,
    });
};

window.trackReservationClickCancelArchivedReservation = function (reservationId) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack('Reservations - Click Cancel Archived Reservation', {
        reservation_id: reservationId || null,
    });
};

window.trackReservationArchivedReservationSuccess = function (reservationId) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack('Reservations - Archived Reservation Success', {
        reservation_id: reservationId || null,
    });
};

window.trackReservationArchivedReservationFail = function (reservationId, errorMessage = null) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack('Reservations - Archived Reservation Fail', {
        reservation_id: reservationId || null,
        error_message: errorMessage || null,
    });
};

window.trackReservationClickApproveFuelClaim = function (reservationId) {
    if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Approve Fuel Claim', {
        reservation_id: reservationId || null,
    });
};

window.trackReservationClickRejectFuelClaim = function (reservationId) {
    if (!window.amplitudeTracker) return;

        window.amplitudeTracker._safeTrack('Reservations - Click Reject Fuel Claim', {
        reservation_id: reservationId || null,
    });
};

// Fuel Claim detail navigation (list -> detail, detail -> back)
window.trackReservationClickViewDetailFuelClaim = function (reservationId, invoiceNumber) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack(
        'Reservations - Click View Detail Fuel Claim',
        {
            reservation_id:
                reservationId != null && String(reservationId).trim() !== ''
                    ? String(reservationId).trim()
                    : null,
            invoice_number:
                invoiceNumber != null && String(invoiceNumber).trim() !== ''
                    ? String(invoiceNumber).trim()
                    : null
        }
    );
    window.amplitudeTracker.flush?.();
};

window.trackReservationClickBackViewDetailFuelClaim = function () {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack(
        'Reservations - Click Back View Detail Fuel Claim',
        {}
    );
    window.amplitudeTracker.flush?.();
};

// Fuel Claim approve/reject results (after redirect)
window.trackReservationApproveFuelClaimSuccess = function (reservationId) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack(
        'Reservations - Approve Fuel Claim Success',
        {
            reservation_id:
                reservationId != null && String(reservationId).trim() !== ''
                    ? String(reservationId).trim()
                    : null
        }
    );
    window.amplitudeTracker.flush?.();
};

window.trackReservationApproveFuelClaimFail = function (
    reservationId,
    errorCode = null,
    errorMessage = null
) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack(
        'Reservations - Approve Fuel Claim Fail',
        {
            reservation_id:
                reservationId != null && String(reservationId).trim() !== ''
                    ? String(reservationId).trim()
                    : null,
            error_code: errorCode != null ? String(errorCode) : null,
            error_message: errorMessage != null ? String(errorMessage) : null
        }
    );
    window.amplitudeTracker.flush?.();
};

window.trackReservationRejectFuelClaimSuccess = function (reservationId) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack(
        'Reservations - Reject Fuel Claim Success',
        {
            reservation_id:
                reservationId != null && String(reservationId).trim() !== ''
                    ? String(reservationId).trim()
                    : null
        }
    );
    window.amplitudeTracker.flush?.();
};

window.trackReservationRejectFuelClaimFail = function (
    reservationId,
    errorCode = null,
    errorMessage = null
) {
    if (!window.amplitudeTracker) return;

    window.amplitudeTracker._safeTrack(
        'Reservations - Reject Fuel Claim Fail',
        {
            reservation_id:
                reservationId != null && String(reservationId).trim() !== ''
                    ? String(reservationId).trim()
                    : null,
            error_code: errorCode != null ? String(errorCode) : null,
            error_message: errorMessage != null ? String(errorMessage) : null
        }
    );
    window.amplitudeTracker.flush?.();
};

/**
 * Admin update success/fail (session `update_result`): use menu "Admins" + admin_id so names
 * stay distinct from getCurrentMenu() "Customers" on `/adminmanagement/*`. List/detail Edit
 * does not send a click event — edit page uses `trackCustomerUpdateCustomerClick` etc.
 */
function _amplitudeAdminUpdateTrackOptions(entity, entityId) {
    if (String(entity || '').toLowerCase() !== 'admin') {
        return {};
    }
    return {
        menuName: 'Admins',
        extraProps: buildEntityIdProperty('admin', entityId)
    };
}

/* ================= UPDATE RESULT EVENTS ================= */
window.trackGlobalUpdateClick = function (entity, entityId) {
    if (!window.amplitudeTracker) return;

    if (!entity || !entityId) {
        console.warn('[Amplitude] Missing entity or entityId for update click');
        return;
    }

    if (String(entity || '').toLowerCase() === 'admin') {
        return;
    }

    const tracker = window.amplitudeTracker._trackAction('update', entity, entityId, {
        actionLabel: 'Update'
    });

    tracker.click();
};

window.trackGlobalUpdateSuccess = function (entity, entityId) {
    if (!window.amplitudeTracker) return;
    
    if (!entity || !entityId) {
        console.warn('[Amplitude] Missing entity or entityId for update success');
        return;
    }

    const tracker = window.amplitudeTracker._trackAction('update', entity, entityId, {
        successLabel: 'Confirm Update',
        ..._amplitudeAdminUpdateTrackOptions(entity, entityId)
    });

    tracker.success();
};

window.trackGlobalUpdateFail = function (entity, entityId, error = null) {
    if (!window.amplitudeTracker) return;
    
    if (!entity || !entityId) {
        console.warn('[Amplitude] Missing entity or entityId for update fail');
        return;
    }

    const tracker = window.amplitudeTracker._trackAction('update', entity, entityId, {
        failLabel: 'Confirm Update',
        ..._amplitudeAdminUpdateTrackOptions(entity, entityId)
    });

    tracker.fail(error);
};

/** Rate Book activate/deactivate — after redirect (session `rate_status_action`). */
window.trackGlobalRateBookStatusSuccess = function (rateStatusAction, entityId) {
    if (!window.amplitudeTracker) return;
    const rid =
        entityId != null && String(entityId).trim() !== ''
            ? String(entityId).trim()
            : null;
    const base = {
        entity: 'rate',
        rate_id: rid,
        entity_id: rid,
        source: 'rate_book_list'
    };
    const m = String(rateStatusAction || '').toLowerCase();
    if (m === 'activate') {
        window.amplitudeTracker._safeTrack('Rates - Activate Rate Book  Success', base);
    } else if (m === 'deactivate') {
        window.amplitudeTracker._safeTrack('Rates - Deactivate Rate Book  Success', base);
    }
    window.amplitudeTracker.flush?.();
};

window.trackGlobalRateBookStatusFail = function (
    rateStatusAction,
    entityId,
    error = null,
    errorCode = null
) {
    if (!window.amplitudeTracker) return;
    const rid =
        entityId != null && String(entityId).trim() !== ''
            ? String(entityId).trim()
            : null;
    const base = {
        entity: 'rate',
        rate_id: rid,
        entity_id: rid,
        source: 'rate_book_list',
        error_code: errorCode ?? null,
        error_message: error ?? null
    };
    const m = String(rateStatusAction || '').toLowerCase();
    if (m === 'activate') {
        window.amplitudeTracker._safeTrack('Rates - Activate Rate Book  Fail', base);
    } else if (m === 'deactivate') {
        window.amplitudeTracker._safeTrack('Rates - Deactivate Rate Book  Fail', base);
    }
    window.amplitudeTracker.flush?.();
};



export default AmplitudeTracker
